"use strict";

// Set to true to enable all v2PDP logs
const V2PDP_DEBUG = false;

function logV2Pdp(...args) {
  if (V2PDP_DEBUG) {
    console.log(...args);
  }
}

function logError(...args) {
  console.error(...args);
}

const v2PdpJs = {
  // Runtime state
  selectedAddOns: new Map(),
  originalSlides: null,
  originalThumbSlides: null,
  youtubeDelegationEl: null,
  youtubeDelegationBound: false,

  // Toggle states controlled by UI
  shields: false,
  floatie: false,
  riser: false,
  noseShield: false,

  // Product/variant data
  productData: null,
  previewDiscount: {
    active: false,
    amountCents: 0,
    source: "",
  },

  // Debounced updater reference
  debouncedUpdatePriceDisplay: null,
  debouncedFilterSlides: null,

  // AB-test state
  abTestEnabled: false,
  testType: "original",
  abObserver: null,

  // Track shield toggle UI state
  isShieldToggleChecked: false,

  // EOL SKUs array - Discontinued SKU variants that should be removed from display
  // Loaded from theme settings (window.EOL_SKUS) - falls back to empty array if not set
  get EOL_SKUS() {
    return window.EOL_SKUS || [];
  },

  // Bound handlers to remove cleanly on destroy
  bound: {
    onAddToCart: null,
    onDocumentChange: null,
    onSizeInfoButtonClick: null,
    onCloseSizeInfoClick: null,
    onSizingDialogBackdropClick: null,
    onShieldToggle: null,
    onFloatieToggle: null,
    onRiserToggle: null,
    onNoseShieldToggle: null,
    onYoutubeDelegation: null,
  },

  /**
   * If URL contains ?frame_size=, reveal the hidden Frame Size fieldset and select the matching radio.
   */
  revealFrameSizeFromUrl() {
    try {
      const url = new URL(window.location.href);
      const fs = url.searchParams.get("frame_size");
      if (!fs) return;

      const normalized = fs.toLowerCase();
      const map = {
        narrow: "Narrow",
        regular: "Regular",
        xl: "XL",
        "extra-large": "XL",
      };

      const targetValue =
        map[normalized] ||
        normalized.charAt(0).toUpperCase() + normalized.slice(1);

      const frameSizeFieldset = document.querySelector(
        "#original-version fieldset:last-child"
      );
      if (!frameSizeFieldset) return;

      // Reveal the fieldset
      frameSizeFieldset.classList.remove("hide-this");

      // Show the selected-option-value if present
      const selectedValueEl = frameSizeFieldset.querySelector(
        ".selected-option-value"
      );
      if (selectedValueEl) {
        selectedValueEl.textContent = targetValue;
      }

      // Try to find and check matching radio
      const radio = frameSizeFieldset.querySelector(
        `input[name="Frame Size"][value="${targetValue}"] , input[name="frame-size"][value="${targetValue}"]`
      );
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } catch (e) {
      logV2Pdp("revealFrameSizeFromUrl error", e);
    }
  },

  /**
   * Init entrypoint: parse product JSON, sync variant in URL, pre-filter slides,
   * set RX state, attach listeners, and initialize AB-test UI safely.
   */
  init() {
    const jsonElement = document.getElementById("v2pdp-json");
    try {
      this.productData = JSON.parse(jsonElement.textContent);
    } catch (error) {
      logV2Pdp("Failed to parse product JSON:", error);
      return;
    }

    this.cachePreviewDiscount();

    const initialVariantId = this.getInitialVariantId();

    // Check if initial variant is EOL and get the replacement
    const initialVariant = this.productData.variants.find(
      (v) => v.id.toString() === initialVariantId.toString()
    );
    let activeVariant = initialVariant;

    if (initialVariant && this.checkEOLVariant(initialVariant)) {
      // checkEOLVariant now handles the switch smoothly
      // Get the updated variant for the rest of initialization
      activeVariant = this.getCurrentVariant() || initialVariant;
    }

    this.updateUrlWithVariantId(activeVariant.id);

    if (activeVariant) {
      // Wait for Swiper to be ready before filtering (Swiper is bootstrapped externally)
      const waitForSwiperAndFilter = () => {
        if (window.productMediaSwiperReady) {
          logV2Pdp("Swiper ready, filtering slides for initial variant");
          this.filterSlides(activeVariant);
          this.checkAndUpdateOOSStatus(activeVariant);

          // Hide skeleton after initial filtering completes
          setTimeout(() => {
            if (window.hideProductMediaSkeleton) {
              window.hideProductMediaSkeleton();
            }
          }, 100);
        } else {
          // Retry until Swiper is ready
          setTimeout(waitForSwiperAndFilter, 50);
        }
      };

      waitForSwiperAndFilter();

      // Make lens fieldset visible after initial setup
      const lensFieldset = document.querySelector(
        ".ombraz-v2pdp__product-options-wrapper fieldset:nth-child(2)"
      );
      if (lensFieldset) {
        lensFieldset.classList.add("lens-options-ready");
      }
    }

    // Wire up UI + listeners
    this.handleV2PdpOptions();
    this.addOnToggleChangePrice();
    this.setupEventListeners();
    this.listenToShields();
    this.setupShieldToggle();
    this.setupFloatieToggle();
    this.setupRiserToggle();
    this.setupNoseShieldToggle();
    this.checkUrlForAddons();
    this.cleanupVariantOptions();
    this.initializeEolSync();
    this.updateAvailablePucks();
    this.updateFrameSizeAvailability();
    this.setupVTOTabToggle();

    // Reveal frame size radios if user arrived from a collection with a size query param
    this.revealFrameSizeFromUrl();

    // Enable AB-test UI management
    this.isAbTestEnabled();
    // Initialize RX state if url has ?rx=true
    const initialRxInput = document.querySelector("[data-rx-option]");
    const initialAtcWrapper = document.querySelector(
      ".ombraz-v2pdp__atc-wrapper"
    );
    const initialRxWrapper = document.querySelector(".rx");
    const initialUrl = new URL(window.location.href);
    const initialRxParam = initialUrl.searchParams.get("rx");

    if (
      initialRxParam === "true" &&
      initialRxInput &&
      initialAtcWrapper &&
      initialRxWrapper
    ) {
      logV2Pdp("[DEBUG] Setting initial state for rx=true param.");
      initialRxInput.checked = true;
      initialAtcWrapper.classList.add("hide-this");
      initialRxWrapper.classList.remove("hide-this");
    }

    // Ensure YouTube clicks work on first load
    window.__v2PdpDelegatedYT = true;
    this.bindYoutubeDelegationOnce();
  },

  /**
   * Clean up listeners and observers to prevent memory leaks across SPA navigations.
   */
  destroy() {
    // Remove ATC listener (ensure reference matches the one used on add)
    const addToCartButton = document.getElementById("v2pdp-availability");
    if (addToCartButton && this.bound.onAddToCart) {
      addToCartButton.removeEventListener("click", this.bound.onAddToCart);
    }

    // Remove price toggle listeners (debounced function is preserved)
    const btns = document.querySelectorAll(".ombraz-v2pdp__toggle-input");
    btns.forEach((btn) => {
      if (this.debouncedUpdatePriceDisplay) {
        btn.removeEventListener("change", this.debouncedUpdatePriceDisplay);
      }
    });
    // Remove shield toggle listener
    const shieldToggle = document.querySelector(
      'input[data-product-type="is_shield"]'
    );
    if (shieldToggle && this.bound.onShieldToggle) {
      shieldToggle.removeEventListener("change", this.bound.onShieldToggle);
    }

    // Remove floatie toggle listener
    const floatieToggle = document.querySelector(
      'input[data-product-type="is_floatie"]'
    );
    if (floatieToggle && this.bound.onFloatieToggle) {
      floatieToggle.removeEventListener("change", this.bound.onFloatieToggle);
    }

    // Remove riser toggle listener
    const riserToggle = document.querySelector(
      'input[data-product-type="is_riser"]'
    );
    if (riserToggle && this.bound.onRiserToggle) {
      riserToggle.removeEventListener("change", this.bound.onRiserToggle);
    }

    // Remove nose shield listener
    const noseShieldToggle = document.querySelector(
      'input[data-product-type="is_nose_shield"], input[name="Nose Shield"]'
    );
    if (noseShieldToggle && this.bound.onNoseShieldToggle) {
      noseShieldToggle.removeEventListener(
        "change",
        this.bound.onNoseShieldToggle
      );
    }

    // Remove delegated listeners
    if (this.bound.onDocumentChange) {
      document.removeEventListener("change", this.bound.onDocumentChange);
    }
    if (this.bound.onSizeInfoButtonClick) {
      const sizeInfoButton = document.getElementById("size-info-button");
      sizeInfoButton?.removeEventListener(
        "click",
        this.bound.onSizeInfoButtonClick
      );
    }
    if (this.bound.onCloseSizeInfoClick) {
      const closeSizeInfoButton = document.querySelector(
        ".size-info-dialog__close-button"
      );
      closeSizeInfoButton?.removeEventListener(
        "click",
        this.bound.onCloseSizeInfoClick
      );
    }
    if (this.bound.onSizingDialogBackdropClick) {
      const sizingDialog = document.getElementById("size-info-dialog");
      sizingDialog?.removeEventListener(
        "click",
        this.bound.onSizingDialogBackdropClick
      );
    }

    // Disconnect AB-test observer
    if (this.abObserver) {
      this.abObserver.disconnect();
      this.abObserver = null;
    }

    if (this.youtubeDelegationEl && this.bound.onYoutubeDelegation) {
      this.youtubeDelegationEl.removeEventListener(
        "click",
        this.bound.onYoutubeDelegation
      );
      this.youtubeDelegationEl = null;
    }
  },

  cachePreviewDiscount() {
    const priceEl = document.getElementById("v2pdp-price");
    if (!priceEl) {
      this.previewDiscount = { active: false, amountCents: 0, source: "" };
      return;
    }

    const rawActive = (priceEl.dataset.previewActive || "false").toLowerCase();
    const rawAmount = Number(priceEl.dataset.previewAmountCents || 0);
    const isActive =
      rawActive === "true" && Number.isFinite(rawAmount) && rawAmount > 0;

    this.previewDiscount = {
      active: isActive,
      amountCents: isActive ? rawAmount : 0,
      source: priceEl.dataset.previewSource || "",
    };
  },

  getPreviewAdjustedPrices(basePrice, rawCompareAtPrice) {
    const variantPrice = Number(basePrice) || 0;
    const fallbackCompareAt = Number(rawCompareAtPrice) || 0;
    const previewAmount = this.previewDiscount?.active
      ? Number(this.previewDiscount.amountCents || 0) / 100
      : 0;

    if (previewAmount > 0) {
      const discountedPrice = Math.max(variantPrice - previewAmount, 0);
      return {
        basePrice: discountedPrice,
        compareAtPrice: variantPrice,
      };
    }

    return {
      basePrice: variantPrice,
      compareAtPrice: fallbackCompareAt,
    };
  },

  /**
   * Event listeners for ATC, sizing dialog sync, and button visibility handling.
   * Uses delegated handlers where possible and stores references for cleanup.
   */
  setupEventListeners() {
    const addToCartButton = document.getElementById("v2pdp-availability");
    if (addToCartButton) {
      this.bound.onAddToCart =
        this.bound.onAddToCart || this.handleV2AddToCart.bind(this);
      addToCartButton.addEventListener("click", this.bound.onAddToCart);
    }

    // Helper to update ATC/OOS visibility based on current variant availability
    const updateButtonVisibility = () => {
      logV2Pdp("Running updateButtonVisibility");
      const variant = this.getCurrentVariant();
      if (variant) {
        logV2Pdp("Found variant, checking availability:", variant.available);
        this.checkAndUpdateOOSStatus(variant);
      } else {
        logV2Pdp("No matching variant found");
      }
    };
    // Frame size sync setup (PDP <-> dialog)
    const frameSizeBtns = document.querySelectorAll(
      ".ombraz-v2pdp__product-options .size-option input[name='frame-size']"
    );
    const dialogSizingBtns = document.querySelectorAll(
      ".size-info-dialog__content-sizing-toggle input[name='size']"
    );
    const syncDialogWithPDP = (selectedValue) => {
      const currentDialogBtns = document.querySelectorAll(
        ".size-info-dialog__content-sizing-toggle input[name='size']"
      );
      currentDialogBtns.forEach((dialogBtn) => {
        if (dialogBtn.value.toLowerCase() === selectedValue.toLowerCase()) {
          dialogBtn.checked = true;
          dialogBtn.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    };

    // Delegate size change handling to document (low churn, safe, fewer listeners)
    if (frameSizeBtns.length && dialogSizingBtns.length) {
      this.bound.onDocumentChange =
        this.bound.onDocumentChange ||
        ((e) => {
          // PDP size -> dialog sync
          if (
            e.target.matches(
              '.ombraz-v2pdp__product-options .size-option input[name="frame-size"]'
            )
          ) {
            const selectedValue = e.target.value.toLowerCase();

            syncDialogWithPDP(selectedValue);
          }

          // Dialog size -> PDP sync
          if (
            e.target.matches(
              '.size-info-dialog__content-sizing-toggle input[name="size"]'
            )
          ) {
            const selectedSize = e.target.value;
            // External helper is assumed global per legacy code
            if (typeof syncPDPWithDialogV2Pdp === "function") {
              syncPDPWithDialogV2Pdp(selectedSize);
            }

            // Show size toggles in product options
            const frameSizeFieldset = document.querySelector(
              "#original-version fieldset:last-child"
            );
            if (frameSizeFieldset) {
              frameSizeFieldset.classList.remove("hide-this");
            }

            // Ensure URL variant reflects current choices with proper sequencing
            requestAnimationFrame(() => {
              const frameColor =
                document.querySelector('input[name="Frame Color"]:checked')
                  ?.value ||
                document.querySelector('input[name="frame_color"]:checked')
                  ?.value;
              const lensColor =
                document.querySelector('input[name="Lens Color"]:checked')
                  ?.value ||
                document.querySelector('input[name="lens_color"]:checked')
                  ?.value ||
                document.querySelector('input[name="Lens Options"]:checked')
                  ?.value ||
                document.querySelector('input[name="lens_options"]:checked')
                  ?.value;
              if (frameColor && lensColor && selectedSize) {
                const options = [frameColor, lensColor, selectedSize];
                const variant = this.productData.variants.find(
                  (v) =>
                    v.option1 === options[0] &&
                    v.option2 === options[1] &&
                    v.option3 === options[2]
                );
                if (variant) {
                  logV2Pdp("Updating URL with variant ID:", variant.id);
                  const productIdElem = document.getElementById("v2-pdp-id");
                  if (productIdElem) {
                    productIdElem.value = variant.id;
                  }
                  this.updateUrlWithVariantId(variant.id, false);

                  // Ensure slides are updated after variant is set
                  requestAnimationFrame(() => {
                    this.filterSlides(variant);
                  });
                }
              }

              // Close the dialog and update buttons after all updates
              requestAnimationFrame(() => {
                const sizingDialog =
                  document.getElementById("size-info-dialog");
                if (sizingDialog) {
                  sizingDialog.close();
                  setTimeout(updateButtonVisibility, 10);
                }
              });
            });
          }
        });
      document.addEventListener("change", this.bound.onDocumentChange);
    }

    // Sync dialog size with PDP size when opening dialog
    const sizeInfoButton = document.getElementById("size-info-button");
    if (sizeInfoButton) {
      this.bound.onSizeInfoButtonClick =
        this.bound.onSizeInfoButtonClick ||
        (() => {
          const sizingDialog = document.getElementById("size-info-dialog");
          if (!sizingDialog) {
            logError("Size info dialog not found!");
            return;
          }

          const currentPdpSize = document.querySelector(
            "#original-version input[name='frame-size']:checked"
          )?.value;

          if (currentPdpSize) {
            const dialogSizeRadio = document.querySelector(
              `#size-info-dialog input[name="size"][value="${currentPdpSize}"]`
            );
            if (dialogSizeRadio) {
              dialogSizeRadio.checked = true;
            }
          }

          sizingDialog.showModal();
        });
      sizeInfoButton.addEventListener(
        "click",
        this.bound.onSizeInfoButtonClick
      );
    }

    // Close button on sizing dialog
    const closeSizeInfoButton = document.querySelector(
      ".size-info-dialog__close-button"
    );
    if (closeSizeInfoButton) {
      this.bound.onCloseSizeInfoClick =
        this.bound.onCloseSizeInfoClick ||
        (() => {
          const sizingDialog = document.getElementById("size-info-dialog");
          if (sizingDialog) {
            sizingDialog.close();
            setTimeout(updateButtonVisibility, 10);
          }
        });
      closeSizeInfoButton.addEventListener(
        "click",
        this.bound.onCloseSizeInfoClick
      );
    }

    // Close dialog when clicking outside
    const sizingDialog = document.getElementById("size-info-dialog");
    if (sizingDialog) {
      this.bound.onSizingDialogBackdropClick =
        this.bound.onSizingDialogBackdropClick ||
        ((event) => {
          if (event.target === sizingDialog) {
            sizingDialog.close();
            setTimeout(updateButtonVisibility, 10);
          }
        });
      sizingDialog.addEventListener(
        "click",
        this.bound.onSizingDialogBackdropClick
      );
    }
  },

  /**
   * Observe body class to switch UI between AB test variants.
   */
  isAbTestEnabled() {
    /**
     * Sets up the UI based on test type.
     * - E-test: Modified sizing UI with hidden VTO button
     * - Original: Default UI with all features
     */
    const setupTest = (testType) => {
      this.testType = testType;
      if (testType === "e-test") {
        this.setupETestUI();
      } else {
        this.setupOriginalUI();
      }
    };
    // Check initial page load state
    if (document.body.classList.contains("e-test")) {
      setupTest("e-test");
    } else {
      setupTest("original");
    }

    // Watch for class changes on body
    this.abObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          if (document.body.classList.contains("e-test")) {
            setupTest("e-test");
          } else {
            setupTest("original");
          }
        }
      });
    });
    this.abObserver.observe(document.body, { attributes: true });
  },

  /**
   * Show the baseline/original UI (VTO enabled, default size flow).
   */
  setupOriginalUI() {
    const elements = {
      originalVersion: document.getElementById("original-version"),
      abtestVersion: document.getElementById("abtest-version"),
      whatMySize: document.querySelector(".is-new-size-btn"),
      sizing: document.querySelector(".v2pdp__abtest-sizing-info"),
      frameSize: document.querySelector(
        "#original-version fieldset:last-child"
      ),
      vtoBtn: document.querySelector(".ombraz-v2pdp-vto"),
      modalVto: document.querySelector(".modal-vto-wrapper"),
      modalHr: document.querySelector(".ombraz-modal__content hr"),
    };
    // Show original elements
    elements.originalVersion?.classList.remove("hide-this");
    elements.whatMySize?.classList.remove("hide-this");
    elements.vtoBtn?.classList.remove("hide-this");
    elements.modalVto?.classList.remove("hide-this");
    elements.modalHr?.classList.remove("hide-this");
    elements.frameSize?.classList.remove("hide-this");
    // Hide test elements
    elements.abtestVersion?.classList.add("hide-this");
    elements.sizing?.classList.add("hide-this");

    this.updateAddOnTitle();
    this.handleV2PdpOptions();
    this.syncVariantWithUrl();
  },

  /**
   * Show the E-test UI (no VTO button, different sizing info).
   */
  setupETestUI() {
    const elements = {
      originalVersion: document.getElementById("original-version"),
      abtestVersion: document.getElementById("abtest-version"),
      atcBtn: document.getElementById("v2pdp-availability"),
      oosBtn: document.querySelector(".oos-btn"),
      sizing: document.querySelector(".v2pdp__abtest-sizing-info"),
      vtoBtn: document.querySelector(".ombraz-v2pdp-vto"),
      whatMySize: document.querySelector(".is-new-size-btn"),
      modalVtoBtn: document.querySelector(".size-info-dialog__vto-btn-wrapper"),
      sizingToggle: document.querySelector(
        ".size-info-dialog__content-sizing-toggle"
      ),
    };
    // Show E-test elements
    elements.originalVersion?.classList.remove("hide-this");
    elements.sizing?.classList.remove("hide-this");
    elements.sizingToggle?.classList.remove("hide-this");

    // Hide unused elements
    elements.abtestVersion?.classList.add("hide-this");
    elements.whatMySize?.classList.add("hide-this");
    elements.vtoBtn?.classList.add("hide-this");
    elements.modalVtoBtn?.classList.add("hide-this");

    // Style updates
    elements.atcBtn.style.backgroundColor = "#9caf89";
    elements.oosBtn.style.backgroundColor = "#9caf89";

    this.handleV2PdpOptions();
    this.syncVariantWithUrl();
  },

  /**
   * Ensure UI radios reflect variant= in URL.
   */
  syncVariantWithUrl() {
    const variantId = this.getVariantIdFromUrl();
    if (variantId) {
      const variantRadio = document.querySelector(
        `#original-version input[value="${variantId}"]`
      );
      if (variantRadio) {
        variantRadio.checked = true;
        variantRadio.dispatchEvent(new Event("change"));
      }
    }
  },

  /**
   * Determine initial variant from checked radios or fallback to first variant.
   */
  getInitialVariantId() {
    const checkedRadio = document.querySelector(
      ".ombraz-v2pdp__product-options input[type=radio]:checked"
    );
    if (checkedRadio) {
      const selectedValues = Array.from(
        document.querySelectorAll(
          ".ombraz-v2pdp__product-options input[type=radio]:checked"
        )
      ).map((radio) => radio.value);
      const matchedVariant = this.productData.variants.find((variant) =>
        variant.options.every((option) => selectedValues.includes(option))
      );
      return matchedVariant ? matchedVariant.id : this.productData.variants?.id;
    }

    return this.productData.variants?.id;
  },

  /**
   * Update URL variant param and optionally sync slides for that variant.
   */
  updateUrlWithVariantId(variantId, skipSlideUpdate = false) {
    const url = new URL(window.location.href);
    const current = url.searchParams.get("variant");

    if (current && current.toString() === variantId.toString()) {
      return;
    }

    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url.toString());

    // Check VTO tab visibility after URL update
    this.checkVTOTabVisibility();

    if (!skipSlideUpdate) {
      const variant = this.productData.variants.find(
        (v) => v.id.toString() === variantId.toString()
      );
      if (variant) this.filterSlides(variant);
    }
  },

  /**
   * Main PDP option logic: variant resolution, price updates, RX handling, and OOS.
   */
  handleV2PdpOptions() {
    const productIdElem = document.getElementById("v2-pdp-id");
    const productPriceElem = document.getElementById("v2pdp-price");
    this.cachePreviewDiscount();

    const selectedOptionElems = document.querySelectorAll(
      "#original-version .selected-option-value"
    );
    const optionRadios = document.querySelectorAll(
      "#original-version .ombraz-v2pdp__product-options input[type=radio]"
    );

    if (!optionRadios.length) return;
    // Refresh radios: clone to remove any stale listeners from past init
    optionRadios.forEach((radio) => {
      const newRadio = radio.cloneNode(true);
      radio.parentNode.replaceChild(newRadio, radio);
    });
    // Re-query fresh radios
    const refreshedRadios = document.querySelectorAll(
      "#original-version .ombraz-v2pdp__product-options input[type=radio]"
    );
    // Filter available lens options based on selected frame color
    const filterLensOptions = (selectedFrameColor) => {
      const lensOptions = document.querySelectorAll(
        '#original-version input[name="Lens Options"], #original-version input[name="Lens Color"]'
      );
      let lensWasSameAsBefore = false;
      let currentlySelectedLens = null;
      let firstAvailableOption = null;
      let matchingLensOption = null;
      const availableOptions = [];
      // STEP 1: Detect currently selected lens option BEFORE any changes
      lensOptions.forEach((lensOption) => {
        if (lensOption.checked && !lensOption.hasAttribute("data-rx-option")) {
          currentlySelectedLens = lensOption.value;
        }
      });
      // STEP 2: Filter and collect available options WITHOUT clearing selections yet
      lensOptions.forEach((lensOption) => {
        const lensLabel = lensOption.closest(".color-option");
        if (!lensLabel || lensOption.hasAttribute("data-rx-option")) return;

        const lensValue = lensOption.value;

        // RESET visibility
        lensLabel.style.display = "";
        lensLabel.classList.remove("hide-this");

        const variantExists = this.productData.variants.some(
          (variant) =>
            variant.option1 === selectedFrameColor &&
            variant.option2 === lensValue
        );

        if (variantExists) {
          availableOptions.push(lensOption);

          if (!firstAvailableOption) {
            firstAvailableOption = lensOption;
          }

          if (currentlySelectedLens && lensValue === currentlySelectedLens) {
            matchingLensOption = lensOption;
          }
        }
      });
      // STEP 3: Hide unavailable options AFTER collecting
      lensOptions.forEach((lensOption) => {
        const lensLabel = lensOption.closest(".color-option");
        if (!lensLabel || lensOption.hasAttribute("data-rx-option")) return;

        const isAvailable = availableOptions.includes(lensOption);

        if (!isAvailable) {
          lensLabel.classList.add("hide-this");
          if (lensOption.checked) {
            lensOption.checked = false;
          }
        }
      });
      // STEP 4: Smart selection logic
      let selectedOption = null;
      if (matchingLensOption) {
        selectedOption = matchingLensOption;
        lensWasSameAsBefore = true;
      } else if (firstAvailableOption) {
        selectedOption = firstAvailableOption;
        lensWasSameAsBefore = false;
      }

      // STEP 5: Update display text ONLY if we have a selection
      const lensOptionSpan = document.querySelector(
        "#original-version fieldset:nth-child(2) .selected-option-value"
      );
      if (selectedOption) {
        // If the selection hasn't changed (same as before), don't dispatch change event
        if (!lensWasSameAsBefore) {
          selectedOption.checked = true;
          selectedOption.dispatchEvent(new Event("change", { bubbles: true }));
        }

        // display text update
        if (lensOptionSpan) {
          let displayValue = selectedOption.value;
          if (displayValue === "Blue-Blocking") displayValue = "Low Light";
          lensOptionSpan.textContent = displayValue;
        }
      }
    };

    // RX toggles (inline integration) - Hide for Limited Edition (Slate) variants and Refugio
    const rxInput = document.querySelector("[data-rx-option]");
    const rxLabel = document.querySelector(".rx-option");
    const atcWrapper = document.querySelector(".ombraz-v2pdp__atc-wrapper");
    const rxWrapper = document.querySelector(".rx");

    // Function to show/hide RX based on selected frame color
    const updateRxVisibility = (selectedFrameColor) => {
      const productTitle =
        document.querySelector("h1")?.textContent?.toLowerCase().trim() || "";
      const isLESlate =
        productTitle.includes("dolomite") &&
        selectedFrameColor &&
        selectedFrameColor.toLowerCase() === "slate";

      if (rxLabel) {
        if (isLESlate) {
          rxLabel.style.display = "none";
          rxLabel.classList.add("hide-this");
        } else {
          rxLabel.style.display = "";
          rxLabel.classList.remove("hide-this");
        }
      }
    };

    // Initialize RX visibility based on initial frame color selection
    const initialFrameColorRadio = document.querySelector(
      '#original-version input[name="Frame Color"]:checked'
    );
    if (initialFrameColorRadio) {
      updateRxVisibility(initialFrameColorRadio.value);
    }

    if (rxInput) {
      rxInput.addEventListener("change", () => {
        if (rxInput.checked) {
          atcWrapper?.classList.add("hide-this");
          rxWrapper?.classList.remove("hide-this");
        }
      });
    }

    // Attach change handlers to refreshed radios
    refreshedRadios.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        const fromUser = event.isTrusted;

        this.updateAvailablePucks();
        this.updateFrameSizeAvailability();

        const checked = radio.checked;

        // On frame color change: filter lens options and update preview ASAP
        if (radio.name === "Frame Color" && checked) {
          filterLensOptions(radio.value);

          // Update RX visibility based on selected frame color
          updateRxVisibility(radio.value);

          const frameColorSpan = document.querySelector(
            "#original-version fieldset:first-child .selected-option-value"
          );
          if (frameColorSpan) {
            const productTitle =
              document.querySelector("h1")?.textContent?.toLowerCase().trim() ||
              "";
            const isLESlate =
              productTitle.includes("dolomite") &&
              radio.value.toLowerCase() === "slate";

            if (isLESlate) {
              frameColorSpan.innerHTML = `${radio.value}<span class="limited-edition-badge ob-h8 semibold">Limited Edition</span>`;
            } else {
              frameColorSpan.textContent = radio.value;
            }
          }
        }

        const selectedValues = Array.from(refreshedRadios)
          .filter((r) => r.checked)
          .map((r) => r.value);

        const matchedVariant = this.productData.variants.find((variant) =>
          variant.options.every((option, _) => selectedValues.includes(option))
        );

        if (!matchedVariant) return;

        // Check if selected variant is EOL
        if (this.checkEOLVariant(matchedVariant)) {
          return; // Redirect will happen in checkEOLVariant
        }

        // Use debounced filterSlides to prevent race conditions when multiple radios change quickly
        if (
          !radio.closest(
            ".ombraz-v2pdp__product-options-wrapper fieldset:last-of-type"
          )
        ) {
          // Clear any pending filterSlides calls
          if (this.debouncedFilterSlides) {
            this.debouncedFilterSlides.cancel?.();
          }

          // Create debounced function if it doesn't exist
          if (!this.debouncedFilterSlides) {
            this.debouncedFilterSlides = this.debounce((variant) => {
              requestAnimationFrame(() => {
                this.filterSlides(variant);
              });
            }, 150);
          }

          this.debouncedFilterSlides(matchedVariant);
        }

        // Sync hidden input + URL
        if (productIdElem) productIdElem.value = matchedVariant.id;
        this.updateUrlWithVariantId(matchedVariant.id);

        // Update visible selected labels with proper display value mapping
        selectedOptionElems.forEach((elem, index) => {
          let displayValue = matchedVariant[`option${index + 1}`];
          if (displayValue === "Blue-Blocking") {
            displayValue = "Low Light";
          }

          // Check if this is LE Slate for badge display on frame color
          const productTitle =
            document.querySelector("h1")?.textContent?.toLowerCase().trim() ||
            "";
          const isLESlate =
            productTitle.includes("dolomite") &&
            matchedVariant.option1 &&
            matchedVariant.option1.toLowerCase() === "slate";

          // For frame color (index 0) with LE Slate, add the badge
          if (isLESlate && index === 0) {
            elem.innerHTML = `${displayValue}<span class="limited-edition-badge ob-h8 semibold">Limited Edition</span>`;
          } else {
            elem.textContent = displayValue;
          }
        });
        // Price setup (store base and compare in dataset for add-on math)
        const variantPrice = matchedVariant.price / 100;
        const variantCompareAtPrice = matchedVariant.compare_at_price / 100;
        const { basePrice, compareAtPrice } = this.getPreviewAdjustedPrices(
          variantPrice,
          variantCompareAtPrice
        );

        if (productPriceElem) {
          productPriceElem.dataset.basePrice = basePrice;
          productPriceElem.dataset.comparePrice = compareAtPrice;
        }
        this.updatePriceDisplay(basePrice, compareAtPrice);
        this.checkAndUpdateOOSStatus(matchedVariant);

        // RX URL/state sync
        if (rxInput && atcWrapper && rxWrapper) {
          const url = new URL(window.location.href);
          const isRxNode = radio.isEqualNode(rxInput);

          if (isRxNode) {
            atcWrapper.classList.add("hide-this");
            rxWrapper.classList.remove("hide-this");
            url.searchParams.set("rx", "true");
            window.history.replaceState({}, "", url.toString());
          } else if (checked && radio.name === rxInput.name) {
            atcWrapper.classList.remove("hide-this");
            rxWrapper.classList.add("hide-this");
            url.searchParams.delete("rx");
            window.history.replaceState({}, "", url.toString());
          }
        }

        if (fromUser) {
          if (productIdElem) productIdElem.value = matchedVariant.id;
          this.updateUrlWithVariantId(matchedVariant.id);
        }
      });
    });

    // Initialize filtering on page load by current frame selection if present
    const initialFrameColor = document.querySelector(
      '#original-version input[name="Frame Color"]:checked'
    );
    if (initialFrameColor) {
      filterLensOptions(initialFrameColor.value);
    }
  },

  /**
   * Map selected variant options to alt text prefix used by product images.
   * This must track the real asset naming patterns including legacy typos.
   */
  mapVariantToAltText(option1, option2) {
    const frameColor = option1.toLowerCase();
    const lenColor = option2.toLowerCase();

    let altText = "";
    const productTitle =
      document.querySelector("h1")?.textContent?.toLowerCase().trim() || "";

    // Check for Limited Edition Dolomite Slate (Slate = LE for Dolomite)
    if (productTitle.includes("dolomite") && frameColor.includes("slate")) {
      altText = "dolomite_slate_brown";
      logV2Pdp("LE Dolomite Slate alt text generated:", altText);
      return altText;
    }

    if (productTitle.includes("refugio")) {
      altText = "refugio_";
      // Frame color mapping - handle both spellings
      if (frameColor.includes("tortoise")) {
        altText += "tortoise_";
      } else if (frameColor.includes("charcoal")) {
        altText += "charcoal_";
      }

      // Lens option mapping based on client's actual images
      if (
        lenColor.includes("grey with black mirror") ||
        lenColor.includes("grey with black")
      ) {
        altText += "black_";
      } else if (
        lenColor.includes("grey with silver") ||
        lenColor.includes("silver") ||
        lenColor.includes("silver flash")
      ) {
        altText += "silver_";
      } else if (
        lenColor.includes("bzonze") || // preserve legacy misspelling in assets
        lenColor.includes("brown with")
      ) {
        altText += "bronze";
      }

      logV2Pdp("Refugio alt text generated:", altText);
      return altText;
    }

    if (frameColor.includes("matte brown")) {
      altText = "mattebrown_";
    } else if (frameColor.includes("black ice")) {
      altText = "black_ice_";
    } else {
      altText = frameColor.replace(" ", "_") + "_";
    }

    if (lenColor.includes("grey") || lenColor.includes("gray")) {
      altText += "grey";
    } else if (lenColor.includes("brown")) {
      altText += "brown";
    } else if (lenColor.includes("yellow")) {
      altText += "yellow";
    } else if (
      lenColor.includes("blue-blocking") ||
      lenColor.includes("low light")
    ) {
      altText += "blocker";
    } else if (lenColor.includes("silver mirror")) {
      altText += "silver_mirror";
    } else if (lenColor.includes("grey mirror")) {
      altText += "grey_mirror";
    } else if (lenColor.includes("blue mirror")) {
      altText += "blue_mirror";
    }

    if (frameColor === "radavist kelp" && lenColor === "yellow polarized") {
      altText = "radavist";
    } else if (frameColor === "frost" && lenColor === "grey mirror") {
      altText = "frost_polarized_grey_mirror";
    }

    return altText;
  },

  /**
   * ATC/OOS toggle logic based on variant availability.
   */
  checkAndUpdateOOSStatus(variant) {
    const addToCartButton = document.getElementById("v2pdp-availability");
    const oosButton = document.querySelector(".oos-btn");

    if (!addToCartButton || !oosButton) return;

    // When user clicks on the OOS button, if there is form has on the parent level, then stop to submit the form.
    oosButton.addEventListener("click", (event) => {
      if (oosButton.closest(".shopify-product-form")) {
        event.preventDefault();
      }
    });

    logV2Pdp("Checking OOS status:", {
      variant: variant?.id,
      available: variant?.available,
      atcButton: addToCartButton ? "found" : "missing",
      oosButton: oosButton ? "found" : "missing",
    });
    if (variant && !variant.available) {
      addToCartButton?.classList.add("hide-this");
      oosButton?.classList.remove("hide-this");
    } else {
      addToCartButton?.classList.remove("hide-this");
      oosButton?.classList.add("hide-this");
    }
  },

  /**
   * Add to cart handler that batches main variant and all selected/available add-ons.
   */
  async handleV2AddToCart(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const items = [];
    const variantId = this.getVariantIdFromUrl();
    if (!variantId) {
      logError("No variant ID found in URL");
      return;
    }

    // Pre-check for unavailable add-ons and block ATC with a helpful modal
    const unavailableAddons = [];
    const addOnInputs = document.querySelectorAll(
      ".ombraz-v2pdp__toggle-input:checked"
    );
    addOnInputs.forEach((input) => {
      const isAvailable = input.dataset.available === "true";
      if (!isAvailable) {
        const toggleCard = input.closest(".ombraz-v2pdp__toggle-card");
        const addonTitle =
          toggleCard.querySelector("h3")?.textContent.trim() || "Add-on";
        unavailableAddons.push(addonTitle);
      }
    });
    if (unavailableAddons.length > 0) {
      this.showUnavailableAddonsModal(unavailableAddons);
      return;
    }

    // Add primary variant
    items.push({
      id: variantId,
      quantity: 1,
    });
    // Add shields if selected and available
    if (this.shields && this.productData.shield_variant?.available) {
      items.push({
        id: this.productData.shield_variant.id,
        quantity: 1,
      });
    }

    // Add floatie if selected and available
    if (this.floatie && this.productData.floatie_variant?.available) {
      items.push({
        id: this.productData.floatie_variant.id,
        quantity: 1,
      });
    }

    // Add other available add-ons
    const availableAddons = Array.from(addOnInputs)
      .filter((input) => input.dataset.available === "true")
      .map((input) => ({
        id: input.value,
        quantity: 1,
      }));
    items.push(...availableAddons);

    try {
      await OmbrazUtils.v2AddToCart(items, {
        button,
        updateSlideCart: true,
      });
    } catch (error) {
      logError("Error adding items to cart:", error);
    }
  },

  /**
   * Wire add-on toggles into price calculation using a debounced display updater.
   */
  addOnToggleChangePrice() {
    const btns = document.querySelectorAll(".ombraz-v2pdp__toggle-input");
    const price = document.getElementById("v2pdp-price");

    if (!btns.length) return;
    this.debouncedUpdatePriceDisplay = this.debounce(
      this.updatePriceDisplay.bind(this),
      100
    );
    btns.forEach((btn) => {
      // Skip riser toggle here; handled by setupRiserToggle for unified behavior
      if (btn.dataset.productType === "is_riser") return;

      btn.addEventListener("change", (event) => {
        logV2Pdp("addontoggle");
        const checked = event.target.checked;
        const pricePayload = this.getAddOnPricePayload(btn);

        if (checked) {
          this.selectedAddOns.set(btn.value, pricePayload);
        } else {
          this.selectedAddOns.delete(btn.value);
        }

        const basePrice = Number.parseFloat(price.dataset.basePrice);
        this.debouncedUpdatePriceDisplay(basePrice);
      });
    });
  },

  getAddOnPricePayload(toggleEl) {
    if (!toggleEl) {
      return { price: 0, comparePrice: 0 };
    }

    const parsedPrice = Number.parseFloat(toggleEl.dataset.toggleProductPrice);
    const parsedCompare = Number.parseFloat(
      toggleEl.dataset.toggleProductComparePrice || "0"
    );

    const normalizedPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
    const normalizedCompare =
      Number.isFinite(parsedCompare) && parsedCompare > normalizedPrice
        ? parsedCompare
        : 0;

    return {
      price: normalizedPrice,
      comparePrice: normalizedCompare,
    };
  },

  /**
   * Helper to detect Refugio to control combined shield/nose behavior.
   */
  isRefugioProduct() {
    const productTitle =
      document.querySelector("h1")?.textContent?.toLowerCase().trim() || "";
    return productTitle.includes("refugio");
  },

  /**
   * Optional: insert floatie slide into a specific position among 3D slides.
   */
  insertFloatieSlide(slidesToAppend, thumbSlidesToAppend, insertPosition = 2) {
    if (!this.floatie) return;
    const floatieSlide = this.originalSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("floatie");
    });
    const floatieThumbSlide = this.originalThumbSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("floatie");
    });
    if (floatieSlide && floatieThumbSlide) {
      const floatieSlideClone = floatieSlide.cloneNode(true);
      const floatieThumbSlideClone = floatieThumbSlide.cloneNode(true);
      slidesToAppend.splice(insertPosition, 0, floatieSlideClone);
      thumbSlidesToAppend.splice(insertPosition, 0, floatieThumbSlideClone);

      logV2Pdp(`🏊‍♂️ Floatie slide inserted at position: ${insertPosition}`);
    }
  },

  /**
   * Append floatie slide at the end of slides array.
   */
  appendFloatieSlide(slidesToAppend, thumbSlidesToAppend) {
    if (!this.floatie) return;
    const floatieSlide = this.originalSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("floatie");
    });
    const floatieThumbSlide = this.originalThumbSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("floatie");
    });
    if (floatieSlide && floatieThumbSlide) {
      const floatieSlideClone = floatieSlide.cloneNode(true);
      const floatieThumbSlideClone = floatieThumbSlide.cloneNode(true);

      slidesToAppend.push(floatieSlideClone);
      thumbSlidesToAppend.push(floatieThumbSlideClone);
      logV2Pdp(
        `🏊‍♂️ Floatie slide appended at position: ${slidesToAppend.length - 1}`
      );
    }
  },

  /**
   * Core: dynamic product gallery filtering based on variant and add-on toggles.
   * Carefully preserves original ordering while selecting relevant media pools.
   */
  filterSlides(selectedVariant) {
    const swiperEl = document.querySelector(".product-media-image--featured");
    const thumbSwiperEl = document.querySelector(
      ".product-media-image--thumbnail"
    );
    const swiper = swiperEl?.swiper;
    const thumbSwiper = thumbSwiperEl?.swiper;
    const APPEND_NORMAL_AFTER_COMBINED = false;

    if (!swiper || !thumbSwiper) {
      logV2Pdp("Swiper instances not found, waiting...");
      setTimeout(() => {
        if (swiperEl?.swiper && thumbSwiperEl?.swiper) {
          logV2Pdp("Retrying filterSlides with ready swipers");
          this.filterSlides(selectedVariant);
        } else {
          logError("Swiper instances still not found after retry");
        }
      }, 100);
      return;
    }

    let expectedAltText = this.mapVariantToAltText(
      selectedVariant.option1,
      selectedVariant.option2
    );
    if (!this.originalSlides) {
      this.originalSlides = Array.from(
        swiperEl.querySelectorAll(".swiper-slide")
      );
      this.originalThumbSlides = Array.from(
        thumbSwiperEl.querySelectorAll(".swiper-slide")
      );
    }

    // Robust 3D/hero detection
    const is3DProductShot = (img) => {
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const src = (img?.src || "").toLowerCase();
      return (
        src.includes("front") ||
        src.includes("side") ||
        src.includes("back") ||
        src.includes("studio") ||
        src.includes("plugs") ||
        alt.includes("render") ||
        alt.includes("hero") ||
        alt.includes("3d")
      );
    };

    // Angle detection from either alt or src
    const getAngle = (img) => {
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const src = (img?.src || "").toLowerCase();
      if (alt.includes("front") || src.includes("front")) return "front";
      if (alt.includes("side") || src.includes("side")) return "side";
      if (alt.includes("back") || src.includes("back")) return "back";
      return "unknown";
    };

    // Ordering and mapping helpers
    const originalIndex = (slide) => this.originalSlides.indexOf(slide);
    const uniqByOriginalIndex = (arr) => {
      const seen = new Set();
      return arr.filter((s) => {
        const i = originalIndex(s);
        if (seen.has(i)) return false;
        seen.add(i);
        return true;
      });
    };
    const inOriginalOrder = (arr) =>
      [...arr].sort((a, b) => originalIndex(a) - originalIndex(b));
    const mapThumbs = (slides) =>
      slides
        .map((s) => this.originalThumbSlides[originalIndex(s)])
        .filter(Boolean);
    const mark3DSlides = (slides) => {
      slides.forEach((slide) => {
        if (!slide.querySelector(".product-media-image__youtube-wrapper")) {
          slide.classList.add("three-d-slide");
        }
      });
    };

    // Filter slides to the current variant by alt text
    const filteredSlides = this.originalSlides.filter((slide) => {
      const img = slide.querySelector("img");
      const alt = img?.getAttribute("alt")?.toLowerCase() || "";
      return alt?.includes(expectedAltText.toLowerCase());
    });
    // Categorize 3D groups
    const slideHas = (img, term) =>
      (img?.getAttribute("alt")?.toLowerCase() || "").includes(term);

    // Helper to check for SIDE shields only (not nose shields)
    const hasSideShieldsOnly = (img) => {
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const src = (img?.src || "").toLowerCase();

      // MUST have "_shields" keyword (underscore + shields)
      const hasShieldsKeyword =
        alt.includes("_shields") ||
        src.includes("_shields") ||
        alt.includes("-shields") ||
        src.includes("-shields");

      // Must NOT have nose_shield variants
      const hasNoseShield =
        alt.includes("nose_shield") ||
        alt.includes("noseshield") ||
        alt.includes("nose shield") ||
        src.includes("nose_shield") ||
        src.includes("noseshield");

      const result = hasShieldsKeyword && !hasNoseShield;

      return result;
    };

    const shield3D = filteredSlides.filter((slide) => {
      const img = slide.querySelector("img");
      return (
        hasSideShieldsOnly(img) &&
        !slideHas(img, "risers") &&
        is3DProductShot(img)
      );
    });
    const riser3D = filteredSlides.filter((slide) => {
      const img = slide.querySelector("img");
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const ref = this.isRefugioProduct();
      const riserOnly = ref
        ? (alt.includes("risers") &&
            !alt.includes("shields") &&
            !alt.includes("nose_shield")) ||
          alt.includes("pads")
        : (alt.includes("risers") && !alt.includes("shields")) ||
          alt.includes("pads");
      return riserOnly;
    });
    const nose3D = filteredSlides.filter((slide) => {
      const img = slide.querySelector("img");
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const src = (img?.src || "").toLowerCase();
      if (!this.isRefugioProduct()) return false;

      // Must have nose_shield variants but NOT have "shields" (plural, side shields)
      const hasNoseShield =
        alt.includes("nose_shield") ||
        alt.includes("noseshield") ||
        alt.includes("nose shield") ||
        src.includes("nose_shield") ||
        src.includes("noseshield");
      const hasSideShields = alt.includes("shields") || src.includes("shields");

      return hasNoseShield && !hasSideShields;
    });
    const combined3D = filteredSlides.filter((slide) => {
      const img = slide.querySelector("img");
      const alt = (img?.getAttribute("alt") || "")
        .toLowerCase()
        .replace(/\n/g, " ");
      const src = (img?.src || "").toLowerCase();

      if (this.isRefugioProduct()) {
        // Combined: BOTH side shields AND nose shields
        const hasSideShields =
          alt.includes("shields") || src.includes("shields");
        const hasNoseShield =
          alt.includes("nose_shield") ||
          alt.includes("noseshield") ||
          alt.includes("nose shield") ||
          src.includes("nose_shield") ||
          src.includes("noseshield");
        return hasSideShields && hasNoseShield;
      } else {
        return alt.includes("shields") && alt.includes("risers");
      }
    });
    const base3D = filteredSlides.filter((slide) => {
      const img = slide.querySelector("img");
      const alt = (img?.getAttribute("alt") || "").toLowerCase();
      const src = (img?.src || "").toLowerCase();

      // Check for ANY shield/addon keywords
      const hasShieldsKeyword =
        alt.includes("_shields") ||
        src.includes("_shields") ||
        alt.includes("-shields") ||
        src.includes("-shields");
      const hasRisers = alt.includes("risers");
      const hasNoseShield =
        alt.includes("nose_shield") ||
        alt.includes("noseshield") ||
        alt.includes("nose shield") ||
        src.includes("nose_shield") ||
        src.includes("noseshield");
      const hasFloatie = alt.includes("floatie");

      let isBase =
        !hasShieldsKeyword &&
        !hasRisers &&
        !hasNoseShield &&
        !hasFloatie &&
        is3DProductShot(img);

      if (this.riser && isBase) {
        isBase =
          !src.includes("visorsoff_front") && !src.includes("visorsoff_side");
      }
      if (this.isRefugioProduct() && this.noseShield && isBase) {
        isBase = !src.includes("noshields") && !src.includes("visorsoff");
      }

      return isBase;
    });

    mark3DSlides(shield3D);
    mark3DSlides(riser3D);
    mark3DSlides(nose3D);
    mark3DSlides(combined3D);
    mark3DSlides(base3D);

    // Ensure up to three angles (front, side, back) by supplementing from pools
    const supplementAngles = (primaryList, supplementPool, maxAngles = 3) => {
      const desiredOrder = ["front", "side", "back"];
      // Map angles in order: first from primary, then from supplement
      const angleToSlide = new Map();
      const putIfMissing = (slide) => {
        const img = slide.querySelector("img");
        const angle = getAngle(img);
        if (desiredOrder.includes(angle) && !angleToSlide.has(angle)) {
          angleToSlide.set(angle, slide);
        }
      };

      primaryList.forEach(putIfMissing);
      if (angleToSlide.size < maxAngles) {
        inOriginalOrder(uniqByOriginalIndex(supplementPool)).forEach(
          putIfMissing
        );
      }

      // Ordered by desired angles
      let result = desiredOrder.map((a) => angleToSlide.get(a)).filter(Boolean);
      // Top up to maxAngles with remaining unique slides from the union
      if (result.length < maxAngles) {
        const union = inOriginalOrder(
          uniqByOriginalIndex([...primaryList, ...supplementPool])
        );
        for (const s of union) {
          if (result.includes(s)) continue;
          result.push(s);
          if (result.length >= maxAngles) break;
        }
      }

      return inOriginalOrder(uniqByOriginalIndex(result));
    };
    // Centralized lifestyle selector (always non-3D, targeted by toggles)
    const pickLifestyleSlides = () => {
      const wantsShieldLifestyle = this.shields;
      const wantsNoseShieldLifestyle =
        this.isRefugioProduct() && this.noseShield && !this.shields;
      const wantsRiserLifestyle =
        this.riser && !this.shields && !this.noseShield;

      // Helper to check for side shields ONLY (not nose shields)
      const hasSideShieldKeyword = (alt) => {
        const hasShields = alt.includes("shields"); // plural
        const hasNoseShield =
          alt.includes("nose_shield") ||
          alt.includes("noseshield") ||
          alt.includes("nose shield");
        return hasShields && !hasNoseShield;
      };

      // Helper to check for nose shields ONLY (not side shields)
      const hasNoseShieldKeyword = (alt) => {
        const hasNoseShield =
          alt.includes("nose_shield") ||
          alt.includes("noseshield") ||
          alt.includes("nose shield");
        const hasShields = alt.includes("shields"); // plural (side shields)
        return hasNoseShield && !hasShields;
      };

      const matchesTopic = (alt) => {
        if (wantsShieldLifestyle)
          return hasSideShieldKeyword(alt) && !alt.includes("risers");
        if (wantsNoseShieldLifestyle) return hasNoseShieldKeyword(alt);
        if (wantsRiserLifestyle)
          return (
            alt.includes("risers") &&
            !hasSideShieldKeyword(alt) &&
            !hasNoseShieldKeyword(alt)
          );
        return (
          !hasSideShieldKeyword(alt) &&
          !hasNoseShieldKeyword(alt) &&
          !alt.includes("risers") &&
          !alt.includes("floatie")
        );
      };

      const candidates = filteredSlides.filter((slide) => {
        const img = slide.querySelector("img");
        if (!img) return false;
        const alt = (img.getAttribute("alt") || "").toLowerCase();
        return !is3DProductShot(img) && matchesTopic(alt);
      });
      return inOriginalOrder(uniqByOriginalIndex(candidates));
    };

    // Normal lifestyle slides without addon keywords
    const getDefaultLifestyleSlides = () => {
      const candidates = filteredSlides.filter((slide) => {
        const img = slide.querySelector("img");
        if (!img) return false;
        const alt = (img.getAttribute("alt") || "").toLowerCase();
        const src = (img.src || "").toLowerCase();
        return (
          !is3DProductShot(img) &&
          !alt.includes("shields") &&
          !src.includes("shields") &&
          !alt.includes("risers") &&
          !alt.includes("nose_shield") &&
          !alt.includes("noseshield") &&
          !alt.includes("nose shield") &&
          !src.includes("nose_shield") &&
          !src.includes("noseshield") &&
          !alt.includes("floatie")
        );
      });
      return inOriginalOrder(uniqByOriginalIndex(candidates));
    };

    let slidesToAppend = [];
    let thumbSlidesToAppend = [];
    // Step 1: choose/supplement the 3D block by toggle state
    if (this.isRefugioProduct()) {
      if (this.shields && this.noseShield) {
        const supplemented = supplementAngles(
          inOriginalOrder(uniqByOriginalIndex(combined3D)),
          [...shield3D, ...nose3D],
          3
        );
        slidesToAppend.push(...supplemented);
      } else if (this.shields) {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(shield3D)));
      } else if (this.noseShield) {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(nose3D)));
      } else {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(base3D)));
      }
    } else {
      if (this.shields && this.riser) {
        // Check if this is Limited Edition Dolomite Slate (Slate = LE)
        const productTitle =
          document.querySelector("h1")?.textContent?.toLowerCase().trim() || "";
        const isLEDolomiteSlate =
          productTitle.includes("dolomite") &&
          selectedVariant &&
          selectedVariant.option1 &&
          selectedVariant.option1.toLowerCase() === "slate";

        if (isLEDolomiteSlate) {
          // For LE Dolomite Slate with both shields and risers, ONLY use combined slides
          const combined = inOriginalOrder(uniqByOriginalIndex(combined3D));
          slidesToAppend.push(...combined);

          // If we don't have enough combined slides, don't supplement - just use what we have
          if (combined.length === 0) {
            logV2Pdp(
              "Warning: No combined slides found for LE Dolomite Slate with shields+risers"
            );
            // Fallback to base only
            slidesToAppend.push(
              ...inOriginalOrder(uniqByOriginalIndex(base3D))
            );
          }
        } else {
          // Normal behavior for other products
          const combined = inOriginalOrder(uniqByOriginalIndex(combined3D));

          if (combined.length) {
            // If combo images exist, use them.
            // ALSO, find any "back" images from the riser-only list and add them too.
            // This is because back-riser shots don't have shields and would be missed.
            const riserBackImages = riser3D.filter((slide) => {
              const img = slide.querySelector("img");
              const alt = (img?.getAttribute("alt") || "").toLowerCase();
              const src = (img?.src || "").toLowerCase();
              // Use the same keywords as is3DProductShot/getAngle
              return alt.includes("back") || src.includes("back");
            });

            // Add both lists together
            slidesToAppend.push(...combined, ...riserBackImages);
          } else {
            // Fallback: If no combo images exist, show shields-only and risers-only.
            slidesToAppend.push(
              ...inOriginalOrder(uniqByOriginalIndex([...shield3D, ...riser3D]))
            );
          }
        }
      } else if (this.shields) {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(shield3D)));
      } else if (this.riser) {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(riser3D)));
      } else {
        slidesToAppend.push(...inOriginalOrder(uniqByOriginalIndex(base3D)));
      }
    }

    // Map thumbs for 3D block
    thumbSlidesToAppend.push(...mapThumbs(slidesToAppend));
    // Step 2: insert floatie immediately after the first 3D slide (index 3)
    if (this.floatie) {
      this.insertFloatieSlide(slidesToAppend, thumbSlidesToAppend, 3);
    }

    // Step 3: append topical lifestyle slides for the toggled topic
    let lifestyleSlides = pickLifestyleSlides();
    // Fallback: default lifestyle if targeted pool is empty
    if (!lifestyleSlides.length) {
      const savedShields = this.shields;
      const savedRiser = this.riser;
      const savedNose = this.noseShield;
      this.shields = false;
      this.riser = false;
      this.noseShield = false;
      lifestyleSlides = pickLifestyleSlides();
      this.shields = savedShields;
      this.riser = savedRiser;
      this.noseShield = savedNose;
    }

    slidesToAppend.push(...lifestyleSlides);
    thumbSlidesToAppend.push(...mapThumbs(lifestyleSlides));
    // For combined toggles including shields or riser, optionally append normal styles after topical styles
    const activeCount =
      (this.shields ? 1 : 0) +
      (this.riser ? 1 : 0) +
      (this.noseShield ? 1 : 0) +
      (this.floatie ? 1 : 0);
    const shouldAppendNormalAfterTopical =
      APPEND_NORMAL_AFTER_COMBINED &&
      activeCount >= 2 &&
      (this.shields || this.riser);
    if (shouldAppendNormalAfterTopical) {
      const normalSlides = getDefaultLifestyleSlides();
      if (normalSlides.length) {
        slidesToAppend.push(...normalSlides);
        thumbSlidesToAppend.push(...mapThumbs(normalSlides));
      }
    }

    // Helper to keep slide/thumb arrays aligned while skipping duplicates
    const dedupeSlidesAndThumbs = (slides, thumbs) => {
      const makeSignature = (slide) => {
        const img = slide?.querySelector?.("img");
        const alt = (img?.getAttribute("alt") || "").trim().toLowerCase();
        const src = (img?.getAttribute("src") || img?.currentSrc || "")
          .split("?")[0]
          .trim()
          .toLowerCase();
        const idxInOriginal = originalIndex(slide);
        if (alt || src) {
          return `${alt}::${src}`;
        }
        return `idx::${idxInOriginal}`;
      };

      const seenSignatures = new Set();
      const dedupedSlides = [];
      const dedupedThumbs = [];

      slides.forEach((slide, idx) => {
        if (!slide) return;
        const signature = makeSignature(slide);
        if (seenSignatures.has(signature)) {
          return;
        }
        seenSignatures.add(signature);

        dedupedSlides.push(slide);
        const thumb = thumbs[idx];
        if (thumb) {
          dedupedThumbs.push(thumb);
        } else {
          const idxInOriginal = originalIndex(slide);
          const fallbackThumb = this.originalThumbSlides[idxInOriginal];
          if (fallbackThumb) {
            dedupedThumbs.push(fallbackThumb);
          }
        }
      });

      return {
        slides: dedupedSlides,
        thumbs: dedupedThumbs,
      };
    };

    // Apply deduplication
    const { slides: dedupedSlides, thumbs: dedupedThumbs } =
      dedupeSlidesAndThumbs(slidesToAppend, thumbSlidesToAppend);
    slidesToAppend = dedupedSlides;
    thumbSlidesToAppend = dedupedThumbs;

    // Ensure hidden styles are removed for active slides
    if (this.shields || this.riser || this.noseShield) {
      slidesToAppend.forEach((slide) => {
        if (
          (this.shields && slide.classList.contains("shield-slide")) ||
          (this.riser && slide.classList.contains("riser-slide")) ||
          (this.noseShield && slide.classList.contains("nose-shield-slide"))
        ) {
          slide.style.removeProperty("display");
        }
      });
      thumbSlidesToAppend.forEach((slide) => {
        if (
          (this.shields && slide.classList.contains("shield-slide")) ||
          (this.riser && slide.classList.contains("riser-slide")) ||
          (this.noseShield && slide.classList.contains("nose-shield-slide"))
        ) {
          slide.style.removeProperty("display");
        }
      });
    }

    // Drop any slides that still carry display:none so Swiper counts stay accurate
    const removeHiddenSlides = (slides) =>
      slides.filter((slide) => {
        const display = slide?.style?.getPropertyValue("display")?.trim();
        const visibility = slide?.style?.getPropertyValue("visibility")?.trim();
        return display !== "none" && visibility !== "hidden";
      });

    slidesToAppend = removeHiddenSlides(slidesToAppend);
    thumbSlidesToAppend = removeHiddenSlides(thumbSlidesToAppend);

    // Update swipers atomically
    swiper.removeAllSlides();
    thumbSwiper.removeAllSlides();
    swiper.appendSlide(slidesToAppend.map((s) => s.cloneNode(true)));
    thumbSwiper.appendSlide(thumbSlidesToAppend.map((s) => s.cloneNode(true)));
    if (swiper.lazy && typeof swiper.lazy.load === "function") {
      swiper.lazy.load();
    }

    swiper.update();
    thumbSwiper.update();
    swiper.slideTo(0, 0, false);
    thumbSwiper.slideTo(0, 0, false);

    // Add Safety Rated badge to the first slide after rebuild
    // This function is defined in product-media-images.liquid
    if (typeof window.addSafetyBadgeToFirstSlide === "function") {
      window.addSafetyBadgeToFirstSlide();
    }

    // Re-attach YouTube video reset handler after rebuild

    // Use a retry mechanism since product-media-images.js might load after v2pdp.js
    const attachYouTubeReset = () => {
      if (typeof window.resetYouTubeVideos === "function") {
        // Get the fresh Swiper instance after rebuild
        const swiperInstance =
          swiper ||
          document.querySelector(".product-media-image--featured")?.swiper;

        if (swiperInstance) {
          // Remove old listener to avoid duplicates
          swiperInstance.off("slideChange", window.resetYouTubeVideos);
          // Re-attach the listener
          swiperInstance.on("slideChange", window.resetYouTubeVideos);
          // Also update global reference
          window.productMediaFeaturedSwiper = swiperInstance;
        }
      } else {
        // Retry after a short delay
        setTimeout(attachYouTubeReset, 100);
      }
    };

    attachYouTubeReset();

    // Re-ensure delegated YT click handler after rebuild
    this.bindYoutubeDelegationOnce();
    logV2Pdp("🎯 Current State:", {
      "🛡️ Shields": this.shields,
      "📏 Riser": this.riser,
      "👃 Nose Shield": this.noseShield,
      "🏊‍♂️ Floatie": this.floatie,
    });
  },

  /**
   * Keep simple boolean state for shields in sync with checkbox.
   */
  listenToShields() {
    const checkbox = document.querySelector(
      'input[data-product-type="is_shield"]'
    );
    if (checkbox) {
      checkbox.addEventListener("change", () => {
        this.shields = checkbox.checked;
      });
    }
  },

  /**
   * Bind shield toggle to slide and URL behavior.
   */
  setupShieldToggle() {
    const shieldToggle = document.querySelector(
      'input[data-product-type="is_shield"]'
    );
    if (shieldToggle) {
      this.bound.onShieldToggle =
        this.bound.onShieldToggle || this.handleShieldToggle.bind(this);
      // preserve bound reference
      shieldToggle.addEventListener("change", this.bound.onShieldToggle);
    }
  },

  /**
   * Bind floatie toggle to slide and URL behavior.
   */
  setupFloatieToggle() {
    const floatieToggle = document.querySelector(
      'input[data-product-type="is_floatie"]'
    );
    if (floatieToggle) {
      this.bound.onFloatieToggle =
        this.bound.onFloatieToggle || this.handleFloatieToggle.bind(this);
      // preserve bound reference
      floatieToggle.addEventListener("change", this.bound.onFloatieToggle);
    }
  },

  /**
   * Shield toggle handler: slides, URL, and scroll on mobile.
   */
  handleShieldToggle(event) {
    this.shields = event.target.checked;
    this.isShieldToggleChecked = event.target.checked;
    // Track shield toggle state

    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.filterSlides(currentVariant);
      if (this.shields) {
        // this.scrollToFeaturedSwiper();
        this.updateUrlWithAddons("sideshields");
      } else {
        this.removeAddonFromUrl("sideshields");
      }
    }
  },

  /**
   * Floatie toggle handler: slides and URL.
   */
  handleFloatieToggle(event) {
    this.floatie = event.target.checked;

    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.filterSlides(currentVariant);
      if (this.floatie) {
        // this.scrollToFeaturedSwiper();
        this.updateUrlWithAddons("floatie");
      } else {
        this.removeAddonFromUrl("floatie");
      }
    }
  },

  /**
   * Bind riser to slide filtering, URL updates, and price updating in one place.
   */
  setupRiserToggle() {
    const riserToggle = document.querySelector(
      'input[data-product-type="is_riser"]'
    );
    logV2Pdp("Riser toggle element:", riserToggle);
    if (riserToggle) {
      logV2Pdp("Adding riser toggle event listener");
      this.bound.onRiserToggle =
        this.bound.onRiserToggle ||
        ((event) => {
          logV2Pdp("Riser toggle changed:", event.target.checked);
          this.riser = event.target.checked;
          logV2Pdp("Riser state:", this.riser);

          // Handle price update for riser
          const pricePayload = this.getAddOnPricePayload(riserToggle);
          if (this.riser) {
            this.selectedAddOns.set(riserToggle.value, pricePayload);
          } else {
            this.selectedAddOns.delete(riserToggle.value);
          }

          // Update price display
          const price = document.getElementById("v2pdp-price");
          const basePrice = Number.parseFloat(price.dataset.basePrice);
          this.debouncedUpdatePriceDisplay(basePrice);

          // Handle variant and URL updates
          const currentVariant = this.getCurrentVariant();

          if (currentVariant) {
            this.filterSlides(currentVariant);
            if (this.riser) {
              // this.scrollToFeaturedSwiper();
              this.updateUrlWithAddons("riser");
            } else {
              this.removeAddonFromUrl("riser");
            }
          }
        });
      riserToggle.addEventListener("change", this.bound.onRiserToggle);
    }
  },

  /**
   * Bind nose shield to slide filtering, URL, and price updates.
   */
  setupNoseShieldToggle() {
    const noseShieldToggle = document.querySelector(
      'input[data-product-type="is_nose_shield"], input[name="Nose Shield"]'
    );
    if (noseShieldToggle) {
      this.bound.onNoseShieldToggle =
        this.bound.onNoseShieldToggle ||
        ((event) => {
          this.noseShield = event.target.checked;

          // Handle price update for nose shield
          const pricePayload = this.getAddOnPricePayload(noseShieldToggle);
          if (this.noseShield) {
            this.selectedAddOns.set(noseShieldToggle.value, pricePayload);
          } else {
            this.selectedAddOns.delete(noseShieldToggle.value);
          }

          // Update price display
          const price = document.getElementById("v2pdp-price");
          const basePrice = Number.parseFloat(price.dataset.basePrice);
          this.debouncedUpdatePriceDisplay(basePrice);

          // Handle variant and URL updates
          const currentVariant = this.getCurrentVariant();

          if (currentVariant) {
            this.filterSlides(currentVariant);
            if (this.noseShield) {
              // this.scrollToFeaturedSwiper();
              this.updateUrlWithAddons("noseshield");
            } else {
              this.removeAddonFromUrl("noseshield");
            }
          }
        });
      noseShieldToggle.addEventListener(
        "change",
        this.bound.onNoseShieldToggle
      );
    }
  },

  /**
   * Append addon tokens to the ?addons= query param in URL.
   */
  updateUrlWithAddons(addon) {
    const url = new URL(window.location.href);
    const currentAddons = url.searchParams.get("addons") || "";
    const addons = new Set(currentAddons.split(",").filter(Boolean));
    addons.add(addon);
    url.searchParams.set("addons", Array.from(addons).join(","));
    window.history.replaceState({}, "", url.toString());
  },

  /**
   * Remove an addon token from the ?addons= query param in URL.
   */
  removeAddonFromUrl(addon) {
    const url = new URL(window.location.href);
    const currentAddons = url.searchParams.get("addons") || "";
    const addons = new Set(currentAddons.split(",").filter(Boolean));
    addons.delete(addon);
    if (addons.size > 0) {
      url.searchParams.set("addons", Array.from(addons).join(","));
    } else {
      url.searchParams.delete("addons");
    }
    window.history.replaceState({}, "", url.toString());
  },

  /**
   * Parse variant query param from URL.
   */
  getVariantIdFromUrl() {
    return new URL(window.location.href).searchParams.get("variant");
  },

  /**
   * Convenience: resolve current variant object from URL or hidden input.
   */
  getCurrentVariant() {
    let variantId = this.getVariantIdFromUrl();
    if (!variantId) {
      const variantInput = document.getElementById("v2-pdp-id");
      if (variantInput?.value) {
        variantId = variantInput.value;
      }
    }
    if (!variantId) return null;
    return this.productData.variants.find(
      (v) => v.id.toString() === variantId.toString()
    );
  },

  /**
   * Initialize addon checkbox states from ?addons= query on load.
   */
  checkUrlForAddons() {
    const url = new URL(window.location.href);
    const addons = url.searchParams.get("addons");
    if (addons) {
      const addonList = addons.split(",");
      // Handle side shields first to ensure dependent UI is correct
      if (addonList.includes("sideshields")) {
        this.shields = true;
        const shieldToggle = document.querySelector(
          'input[data-product-type="is_shield"]'
        );
        if (shieldToggle) {
          shieldToggle.checked = true;
          shieldToggle.dispatchEvent(new Event("change"));
        }
      }

      // Handle nose shield
      if (addonList.includes("noseshield")) {
        this.noseShield = true;
        const noseShieldToggle = document.querySelector(
          'input[data-product-type="is_nose_shield"], input[name="Nose Shield"]'
        );
        if (noseShieldToggle) {
          noseShieldToggle.checked = true;
          noseShieldToggle.dispatchEvent(new Event("change"));
        }
      }

      // Handle floatie
      if (addonList.includes("floatie")) {
        this.floatie = true;
        const floatieToggle = document.querySelector(
          'input[data-product-type="is_floatie"]'
        );
        if (floatieToggle) {
          floatieToggle.checked = true;
          floatieToggle.dispatchEvent(new Event("change"));
        }
      }

      // Handle riser
      if (addonList.includes("riser")) {
        this.riser = true;
        const riserToggle = document.querySelector(
          'input[data-product-type="is_riser"]'
        );
        if (riserToggle) {
          riserToggle.checked = true;
          riserToggle.dispatchEvent(new Event("change"));
        }
      }
    }
  },

  /**
   * Compute total price including selected add-ons (expects base in major units).
   */
  calculateTotalPrice(basePrice, valueType = "price") {
    let totalPrice = Number(basePrice) || 0;

    const targetKey = valueType === "compare" ? "comparePrice" : "price";

    this.selectedAddOns.forEach((entry) => {
      if (entry && typeof entry === "object") {
        const value = Number(entry[targetKey]) || 0;
        if (targetKey == "comparePrice") {
          totalPrice += value + entry["price"];
        } else {
          totalPrice += value;
        }
      } else {
        totalPrice += Number(entry) || 0;
      }
    });

    return Math.round(totalPrice * 100) / 100;
  },

  /**
   * Format and render price with optional compare-at if a true discount exists.
   */
  updatePriceDisplay(basePrice, compareAtPrice) {
    const price = document.getElementById("v2pdp-price");
    if (!price) return;

    compareAtPrice = compareAtPrice || Number(price.dataset.comparePrice) || 0;
    basePrice = Number(basePrice) || 0;

    const totalPrice = this.calculateTotalPrice(basePrice, "price");
    const totalCompareAtPrice = this.calculateTotalPrice(
      compareAtPrice,
      "compare"
    );
    let totalPriceString = OmbrazUtils.formatMoneyInCents(
      totalPrice * 100
    ).toString();
    if (
      totalCompareAtPrice > 0 &&
      totalCompareAtPrice > totalPrice &&
      totalCompareAtPrice !== totalPrice
    ) {
      const totalCompareAtString = OmbrazUtils.formatMoneyInCents(
        totalCompareAtPrice * 100
      ).toString();
      totalPriceString = `<span class="compare-at-price">${totalCompareAtString}</span> ${totalPriceString}`;
    }
    price.innerHTML = totalPriceString;
    price.setAttribute("aria-live", "polite");
  },

  /**
   * Utilities to fetch representative image URLs for add-on tooltips/badges.
   */
  getFloatieImageUrl() {
    if (!this.originalSlides) return null;
    const floatieSlide = this.originalSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("floatie");
    });
    if (floatieSlide) {
      const img = floatieSlide.querySelector("img");
      return img?.src || null;
    }

    return null;
  },

  getRiserImageUrl() {
    if (!this.originalSlides) return null;
    const riserSlide = this.originalSlides.find((slide) => {
      const img = slide.querySelector("img");
      return img?.alt.toLowerCase().includes("riser");
    });
    if (riserSlide) {
      const img = riserSlide.querySelector("img");
      return img?.src || null;
    }

    return null;
  },

  /**
   * Enhanced debounce helper with cancel support for race condition prevention.
   */
  debounce(func, wait) {
    let timeout;
    const debounced = (...args) => {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };

    // Add cancel method to clear pending executions
    debounced.cancel = () => {
      clearTimeout(timeout);
    };

    return debounced;
  },

  /**
   * Smooth-scroll to featured media on mobile for context after toggling.
   */
  scrollToFeaturedSwiper() {
    const featuredSwiper = document.querySelector(
      ".product-media-image--featured"
    );
    if (featuredSwiper && window.innerWidth <= 1024) {
      const headerOffset = 80;
      const elementPosition = featuredSwiper.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  },

  /**
   * Scroll to featured media on all devices (used for sizing-stack selection).
   */
  scrollToFeaturedSwiperAllDevices() {
    const featuredSwiper = document.querySelector(
      ".product-media-image--featured"
    );
    if (featuredSwiper) {
      const headerOffset = 80;
      const elementPosition = featuredSwiper.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  },

  /**
   * Prune lens options based on product type (polarized/non-polarized/blue-blocking).
   * Works with both normalized and legacy Shopify variant structures.
   * This ensures old combined products don't show incompatible lens options.
   */
  cleanupVariantOptions() {
    // Get product type from data attribute (set in Liquid template)
    const optionsWrapper = document.getElementById("original-version");
    const productType = optionsWrapper?.dataset?.productType || "";

    // If no product type is set, try to determine from selected option
    const selectedOption = document.querySelector(
      'input[name="Lens Options"]:checked, input[name="Lens Color"]:checked'
    );

    let selectedValue = "";
    if (selectedOption) {
      selectedValue = selectedOption.value.toLowerCase();
    } else if (productType) {
      // Use product type to determine what to show/hide
      selectedValue = productType;
    } else {
      return; // Can't determine what to filter
    }

    // Determine product type - check both selectedValue and productType data attribute
    const isPolarized =
      productType === "polarized" ||
      (selectedValue.includes("polarized") &&
        !selectedValue.includes("non-polarized"));
    const isNonPolarized =
      productType === "non-polarized" ||
      selectedValue.includes("non-polarized");
    const isBlueBlocking =
      productType === "blue-blocking" ||
      selectedValue.includes("blue-blocking") ||
      selectedValue.includes("low light");
    const lensFieldset = document.querySelector(
      ".ombraz-v2pdp__product-options-wrapper fieldset:nth-child(2)"
    );
    if (!lensFieldset) return;
    const lensOptions = lensFieldset.querySelectorAll(".color-option");

    lensOptions.forEach((option) => {
      const input = option.querySelector("input");
      if (!input) return;

      const value = input.value.toLowerCase();

      // Skip RX option from cleanup
      if (input.hasAttribute("data-rx-option")) {
        logV2Pdp("Skipping RX option:", input);
        return;
      }

      if (
        isPolarized &&
        (value.includes("non-polarized") ||
          value.includes("blue-blocking") ||
          value.includes("low light"))
      ) {
        option.remove();
      } else if (isNonPolarized && !value.includes("non-polarized")) {
        option.remove();
      } else if (
        isBlueBlocking &&
        !(value.includes("blue-blocking") || value.includes("low light"))
      ) {
        // Prevent duplicate slides (e.g., Refugio nose shield shots) from being appended twice
        const deduped = dedupeSlidesAndThumbs(
          slidesToAppend,
          thumbSlidesToAppend
        );
        slidesToAppend = deduped.slides;
        thumbSlidesToAppend = deduped.thumbs;
        option.remove();
      }
    });
  },

  /**
   * Accessible modal listing unavailable add-ons when ATC is blocked.
   */
  showUnavailableAddonsModal(unavailableAddons) {
    const existingModal = document.querySelector(
      ".ombraz-v2pdp__modal-overlay"
    );
    if (existingModal) existingModal.remove();

    const modalHtml = `
      <div class="ombraz-v2pdp__modal-overlay" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" aria-modal="true">
        <div class="ombraz-v2pdp__modal">
          <button class="ombraz-v2pdp__modal-close" aria-label="Close dialog">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="ombraz-v2pdp__modal-content" aria-live="polite">
            <div class="modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#FF4A4A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 id="modal-title">Unavailable Items</h3>
            <div id="modal-description">
              <p class="modal-message" role="alert">The following items are currently out of stock:</p>
              <ul class="unavailable-items" role="list">
                ${unavailableAddons
                  .map(
                    (addon) => `
                  <li role="listitem">
                    <span class="item-name">${addon.replace(
                      /\+(\$\d+)/,
                      " +$1"
                    )}</span>
                  </li>
                `
                  )
                  .join("")}
              </ul>
              <p class="modal-suggestion">Please remove these items to proceed with your purchase.</p>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML("beforeend", modalHtml);

    this.setupModalEventListeners();
  },

  /**
   * Modal UX bindings: close button, backdrop click, Esc key.
   */
  setupModalEventListeners() {
    const modalOverlay = document.querySelector(".ombraz-v2pdp__modal-overlay");
    const closeButton = document.querySelector(".ombraz-v2pdp__modal-close");
    const modalContent = document.querySelector(".ombraz-v2pdp__modal");
    if (!modalOverlay || !closeButton || !modalContent) {
      logError("Modal elements not found");
      return;
    }

    // Close when clicking the close button
    closeButton.addEventListener("click", () => {
      modalOverlay.remove();
    });
    // Close when clicking outside the modal
    modalOverlay.addEventListener("click", (event) => {
      if (event.target === modalOverlay) {
        modalOverlay.remove();
      }
    });
    // Prevent clicks on the modal content from closing the modal
    modalContent.addEventListener("click", (event) => {
      event.stopPropagation();
    });
    // Add keyboard support (ESC to close)
    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        document.querySelector(".ombraz-v2pdp__modal-overlay")
      ) {
        modalOverlay.remove();
      }
    });
  },

  /**
   * Legacy RX handler kept for compatibility; RX is integrated in handleV2PdpOptions.
   */
  handleRxOption() {
    logV2Pdp("handleRxOption");
    const rxInput = document.querySelector("[data-rx-option]");
    const atcWrapper = document.querySelector(".ombraz-v2pdp__atc-wrapper");
    const rxWrapper = document.querySelector(".rx");
    logV2Pdp({ rxInput, atcWrapper, rxWrapper });

    if (!rxInput || !atcWrapper || !rxWrapper) return;
    // Check URL for rx parameter on load
    const url = new URL(window.location.href);
    const rxParam = url.searchParams.get("rx");
    if (rxParam === "true") {
      rxInput.checked = true;
      atcWrapper.classList.add("hide-this");
      rxWrapper.classList.remove("hide-this");
    }

    rxInput.addEventListener("change", (event) => {
      const url = new URL(window.location.href);

      if (event.target.checked) {
        atcWrapper.classList.add("hide-this");
        rxWrapper.classList.remove("hide-this");
        url.searchParams.set("rx", "true");
      } else {
        atcWrapper.classList.remove("hide-this");
        rxWrapper.classList.add("hide-this");
        url.searchParams.delete("rx");
      }

      window.history.replaceState({}, "", url.toString());
    });

    const otherLensOptions = document.querySelectorAll(
      'input[name="Lens Options"], input[name="Lens Color"]'
    );
    otherLensOptions.forEach((option) => {
      if (option !== rxInput) {
        option.addEventListener("change", () => {
          if (option.checked) {
            rxInput.checked = false;
            atcWrapper.classList.remove("hide-this");
            rxWrapper.classList.add("hide-this");
            const url = new URL(window.location.href);
            url.searchParams.delete("rx");
            window.history.replaceState({}, "", url.toString());
          }
        });
      }
    });
  },

  bindYoutubeDelegationOnce() {
    const featured = document.querySelector(".product-media-image--featured");
    if (!featured) return;
    // Rebind if container changed (handles rebuilds/replacements)
    if (this.youtubeDelegationEl !== featured) {
      // Remove old listener if any
      if (this.youtubeDelegationEl && this.bound.onYoutubeDelegation) {
        this.youtubeDelegationEl.removeEventListener(
          "click",
          this.bound.onYoutubeDelegation
        );
      }

      this.youtubeDelegationEl = featured;

      // Create (or reuse) the delegated handler
      this.bound.onYoutubeDelegation =
        this.bound.onYoutubeDelegation ||
        ((e) => {
          const btn = e.target.closest(
            ".product-media-image__youtube-wrapper button"
          );
          if (!btn || !featured.contains(btn)) return;

          e.stopPropagation();

          const videoId = btn.getAttribute("data-video-id");
          const slide = btn.closest(".product-media-image__slide");
          const wrapper = btn.closest(".product-media-image__youtube-wrapper");
          const videoContainer = slide?.querySelector(
            ".product-media-image__video-container"
          );
          const iframe = slide?.querySelector(
            ".product-media-image__video-iframe"
          );

          if (videoId && iframe && videoContainer && wrapper) {
            iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&showinfo=0`;
            wrapper.style.display = "none";
            videoContainer.style.display = "block";

            // Match template behavior for desktop height adjustment
            if (window.matchMedia("(min-width: 560px)").matches) {
              const featuredSlider = document.querySelector(
                ".product-media-image--featured"
              );
              if (
                featuredSlider &&
                window.matchMedia("(min-width: 560px)").matches
              ) {
                featuredSlider.style.height = "430px";
              }
            }
          }
        });
      featured.addEventListener("click", this.bound.onYoutubeDelegation);
      this.youtubeDelegationBound = true;
    }
  },

  /**
   * Display EOL redirect popup with countdown and auto-redirect
   * @param {string} redirectUrl - The URL to redirect to
   * @param {string} productTitle - The product title for display
   *
   * TO ENABLE THIS FEATURE: Uncomment the call in checkEOLVariant()
   * TO DISABLE THIS FEATURE: Comment out the call in checkEOLVariant()
   */
  eolRedirectPopup(redirectUrl, productTitle) {
    // Prevent multiple modals from showing
    if (document.querySelector(".eol-modal")) {
      logV2Pdp("EOL modal already showing, skipping...");
      return;
    }

    // Get template and clone it
    const template = document.getElementById("eol-redirect-template");
    if (!template) {
      // Fallback: simple redirect if template not found
      window.location.href = redirectUrl;
      return;
    }

    const modalContent = template.content.cloneNode(true);

    // Build detailed variant description
    let variantDetails = productTitle;

    // Get selected options from UI
    const selectedFrameColor = document.querySelector(
      '#original-version input[name="Frame Color"]:checked'
    );
    const selectedLens = document.querySelector(
      '#original-version input[name="Lens Options"]:checked, #original-version input[name="Lens Color"]:checked'
    );
    const selectedSize = document.querySelector(
      '#original-version input[name="Frame Size"]:checked'
    );

    // Build variant string: "Product / Color / Lens / Size"
    const parts = [productTitle];

    if (selectedFrameColor?.value) {
      parts.push(selectedFrameColor.value);
    }

    if (selectedLens?.value) {
      // Convert "Blue-Blocking" to "Low Light" for display
      const lensValue =
        selectedLens.value === "Blue-Blocking"
          ? "Low Light"
          : selectedLens.value;
      parts.push(lensValue);
    }

    if (selectedSize?.value) {
      parts.push(selectedSize.value);
    }

    // Join with " / " separator
    variantDetails = parts.join(" / ");

    // Update dynamic content
    const messageElement = modalContent.querySelector("[data-eol-message]");
    if (messageElement) {
      messageElement.textContent = `${variantDetails} is no longer available.`;
    }

    // Add to DOM
    document.body.appendChild(modalContent);

    // Get references after appending to DOM (use setTimeout to ensure DOM is ready)
    setTimeout(() => {
      const modal = document.querySelector(".eol-modal");
      const progressFill = document.querySelector(".eol-progress-fill");
      const button = document.querySelector("[data-eol-redirect]");
      const countdownElement = document.querySelector(".eol-countdown");

      if (!modal || !progressFill || !button || !countdownElement) {
        logError("EOL modal elements not found");
        window.location.href = redirectUrl;
        return;
      }

      // Start progress bar animation
      requestAnimationFrame(() => {
        progressFill.style.width = "100%";
      });

      // Countdown timer
      let countdown = 3;
      const countdownInterval = setInterval(() => {
        countdown--;
        countdownElement.textContent = countdown.toString();

        if (countdown <= 0) {
          clearInterval(countdownInterval);
          window.location.href = redirectUrl;
        }
      }, 1000);

      // Button click handler
      button.onclick = () => {
        clearInterval(countdownInterval);
        window.location.href = redirectUrl;
      };
    }, 50);
  },

  checkEOLVariant(variant) {
    if (!variant || !variant.sku) return false;
    // Convert SKU to string for comparison (EOL_SKUS contains strings)
    const isEOL = this.EOL_SKUS.includes(String(variant.sku));

    if (isEOL) {
      logV2Pdp("EOL variant detected:", variant.sku);

      // Record variant ID for remapping (as required by SOW)
      console.log(
        "[EOL_VARIANT_REMOVED] Variant ID:",
        variant.id,
        "SKU:",
        variant.sku,
        "Title:",
        variant.title
      );

      // Also log to dataLayer for analytics tracking if available
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "eol_variant_removed",
          variant_id: variant.id,
          variant_sku: variant.sku,
          variant_title: variant.title,
          product_id: this.productData?.id || null,
        });
      }

      // Find THE VERY FIRST available non-EOL variant (not similar, just first available)
      const firstAvailableVariant = this.productData.variants.find(
        (v) => v.available && !this.EOL_SKUS.includes(v.sku)
      );

      if (firstAvailableVariant) {
        logV2Pdp(
          "Switching to first available variant:",
          firstAvailableVariant.id
        );
        // COMPLETELY reset to first available variant
        this.resetToFirstAvailableVariant(firstAvailableVariant);
        return true;
      } else {
        // No available non-EOL variants found - product is fully discontinued
        logV2Pdp("⚠️ All variants are EOL. Redirecting to collection page.");

        // Extract product name for dynamic redirect
        const productTitleElement = document.querySelector(
          ".ombraz-v2pdp__product-title h1"
        );
        const productTitle = productTitleElement?.textContent || "This product";
        const productNameLower = productTitle.toLowerCase().trim();

        // Determine collection filter based on product name
        let collectionFilter = "";
        const productStyles = [
          "leggero",
          "dolomite",
          "classic",
          "cammina",
          "viale",
          "teton",
          "refugio",
        ];

        for (const style of productStyles) {
          if (productNameLower.includes(style)) {
            collectionFilter = `?styles=${style}`;
            break;
          }
        }

        const redirectUrl = `/collections/shop-all${collectionFilter}`;

        // =================================================================
        // FEATURE TOGGLE: EOL Redirect Popup
        // =================================================================
        // Option 1: Show popup with countdown (COMMENTED OUT - DISABLED)
        // Uncomment the line below to enable the EOL redirect popup
        // this.eolRedirectPopup(redirectUrl, productTitle);

        // Option 2: Direct redirect without popup (ACTIVE)
        // Comment out the line below to disable direct redirect
        window.location.href = redirectUrl;
        // =================================================================

        return true;
      }
    }

    return false;
  },

  /**
   * Update radio button selections to match the new variant - Enhanced Version
   */
  updateRadioSelections(variant) {
    logV2Pdp("Setting radio selections for variant:", variant.options);
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      // Update Frame Color (option1)
      if (variant.option1) {
        const frameColorRadio = document.querySelector(
          `#original-version input[name="Frame Color"][value="${variant.option1}"]`
        );
        if (frameColorRadio) {
          frameColorRadio.checked = true;
          logV2Pdp("✓ Selected Frame Color:", variant.option1);
        }
      }

      // Update Lens Option (option2)
      if (variant.option2) {
        const lensRadio = document.querySelector(
          `#original-version input[name="Lens Options"][value="${variant.option2}"], ` +
            `#original-version input[name="Lens Color"][value="${variant.option2}"]`
        );
        if (lensRadio) {
          lensRadio.checked = true;
          logV2Pdp("✓ Selected Lens Option:", variant.option2);
        }
      }

      // Update Frame Size (option3) - THIS IS KEY!
      if (variant.option3) {
        const frameSizeRadio = document.querySelector(
          `#original-version input[name="Frame Size"][value="${variant.option3}"], ` +
            `#original-version input[name="frame-size"][value="${variant.option3}"]`
        );
        if (frameSizeRadio) {
          frameSizeRadio.checked = true;
          logV2Pdp("✓ Selected Frame Size:", variant.option3);
        } else {
          logV2Pdp("❌ Could not find Frame Size radio for:", variant.option3);
        }
      }
    }, 100);
  },

  /**
   * Update variant-related UI elements (price, buttons, etc.)
   */
  updateVariantUI(variant) {
    const productIdElem = document.getElementById("v2-pdp-id");
    const productPriceElem = document.getElementById("v2pdp-price");
    this.cachePreviewDiscount();

    // Update hidden input
    if (productIdElem) {
      productIdElem.value = variant.id;
    }

    // Update price
    const variantPrice = variant.price / 100;
    const variantCompareAtPrice = variant.compare_at_price / 100;
    const { basePrice, compareAtPrice } = this.getPreviewAdjustedPrices(
      variantPrice,
      variantCompareAtPrice
    );

    if (productPriceElem) {
      productPriceElem.dataset.basePrice = basePrice;
      productPriceElem.dataset.comparePrice = compareAtPrice;
      this.updatePriceDisplay(basePrice, compareAtPrice);
    }

    // Update availability buttons
    this.checkAndUpdateOOSStatus(variant);
  },

  /**
   * Update the selected option display text
   */
  updateSelectedOptionText(variant) {
    const selectedOptionElems = document.querySelectorAll(
      "#original-version .selected-option-value"
    );
    selectedOptionElems.forEach((elem, index) => {
      let displayValue = variant[`option${index + 1}`];
      if (displayValue === "Blue-Blocking") {
        displayValue = "Low Light";
      }
      elem.textContent = displayValue;
    });
  },

  /**
   * Completely reset all selections to the first available variant
   */
  resetToFirstAvailableVariant(variant) {
    logV2Pdp("Resetting to first available variant:", variant);
    // Step 1: Clear ALL radio selections completely
    const allRadios = document.querySelectorAll(
      '#original-version input[type="radio"]'
    );
    allRadios.forEach((radio) => {
      radio.checked = false;
    });
    // Step 2: Wait a moment for the DOM to update, then set new selections
    setTimeout(() => {
      // Update all UI elements to match the first available variant
      this.updateVariantUI(variant);
      this.updateRadioSelections(variant);
      this.updateSelectedOptionText(variant);
      this.filterSlides(variant);
      this.updateUrlWithVariantId(variant.id, true); // Skip double slide update

      logV2Pdp("Successfully reset to variant:", variant.options);
    }, 50);
  },

  /**
   * Observe frame size option visibility and sync with sizing accordion.
   */
  initializeEolSync() {
    const frameSizeOptions = document.querySelectorAll(
      ".ombraz-v2pdp__product-options label.size-option"
    );
    if (!frameSizeOptions.length) return;

    const getSizingAccordionItem = (value) => {
      const input = document.querySelector(
        `.sizing-stack__size-option-inner input[value="${value}"]`
      );
      return input ? input.closest(".sizing-stack__size-option") : null;
    };

    const syncVisibility = (frameSizeLabel) => {
      const input = frameSizeLabel.querySelector('input[type="radio"]');
      if (!input) return;

      const value = input.value;
      const sizingItem = getSizingAccordionItem(value);
      if (!sizingItem) return;

      const shouldBeHidden = frameSizeLabel.classList.contains("eol-hidden");
      sizingItem.classList.toggle("eol-hidden", shouldBeHidden);
    };

    frameSizeOptions.forEach((labelEl) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            syncVisibility(labelEl);
          }
        });
      });

      observer.observe(labelEl, {
        attributes: true,
        attributeFilter: ["class"],
      });

      // Initial sync on page load
      syncVisibility(labelEl);
    });
  },

  /**
   * Dynamically shows or hides option pucks based on the current selections
   * to prevent a user from selecting an EOL variant.
   */
  updateAvailablePucks() {
    const rxInput = document.querySelector("[data-rx-option]");
    if (rxInput && rxInput.checked) {
      return; // If RX is selected, stop this function and do nothing.
    }

    const optionsWrapper = document.getElementById("original-version");
    if (!optionsWrapper) return;

    const fieldsets = optionsWrapper.querySelectorAll("fieldset");
    const EOL_SKUS = this.EOL_SKUS;
    const productVariants = this.productData.variants;

    // Get the currently selected options
    const currentSelections = {};
    this.productData.options.forEach((optionName, index) => {
      const fieldset = fieldsets[index];
      const checkedRadio = fieldset?.querySelector(
        'input[type="radio"]:checked'
      );
      if (checkedRadio) {
        currentSelections[optionName] = checkedRadio.value;
      }
    });
    // Iterate through each option group (e.g., "Frame Color", "Lens Options") to evaluate its pucks
    this.productData.options.forEach((optionNameToEvaluate, index) => {
      const fieldset = fieldsets[index];
      if (!fieldset) return;

      const pucksInGroup = fieldset.querySelectorAll('input[type="radio"]');

      pucksInGroup.forEach((puck) => {
        // Don't hide the RX option
        if (puck.dataset.rxOption !== undefined) return;

        // Don't process Frame Size with EOL logic - it has its own availability function
        if (puck.name === "frame-size") return;

        let isViable = false;

        // Special handling for Frame Color: Show ALL frame colors that have at least one available variant
        // This allows users to see all color options regardless of lens selection
        if (optionNameToEvaluate === "Frame Color") {
          const frameColorOptionIndex =
            this.productData.options.indexOf("Frame Color");

          isViable = productVariants.some((variant) => {
            // Exclude EOL variants - convert SKU to string for comparison
            if (variant.sku && EOL_SKUS.includes(String(variant.sku)))
              return false;

            // Check if this variant has the frame color we're evaluating
            return variant.options[frameColorOptionIndex] === puck.value;
          });
        } else {
          // For other options (Lens Options, Lens Color, etc.), use cross-filtering
          // Build test criteria from currently selected options
          const testCriteria = {};
          for (const key in currentSelections) {
            if (key !== optionNameToEvaluate) {
              testCriteria[key] = currentSelections[key];
            }
          }
          // Add the current puck's value to the criteria
          testCriteria[optionNameToEvaluate] = puck.value;

          // Check if AT LEAST ONE non-EOL variant exists that matches our test criteria
          isViable = productVariants.some((variant) => {
            // Convert SKU to string for comparison (EOL_SKUS contains strings)
            if (variant.sku && EOL_SKUS.includes(String(variant.sku))) {
              return false; // This variant is EOL, so it can't make the puck viable.
            }

            // Check if the variant matches all our criteria
            let isMatch = true;
            for (const key in testCriteria) {
              const optionIndex = this.productData.options.indexOf(key);
              if (variant.options[optionIndex] !== testCriteria[key]) {
                isMatch = false;
                break;
              }
            }
            return isMatch;
          });
        }

        // Hide or show the puck based on its viability
        const label = puck.closest(".color-option, .size-option");
        if (label) {
          if (isViable) {
            label.classList.remove("eol-hidden");
          } else {
            label.classList.add("eol-hidden");
          }
        }
      });
    });
  },

  /**
   * Update frame size availability in both PDP and "What's my size?" modal
   * based on actual variant availability (out of stock status), independent of EOL.
   * This ensures that if a frame size is completely out of stock for the current
   * frame color and lens selection, it gets marked accordingly.
   */
  updateFrameSizeAvailability() {
    try {
      const optionsWrapper = document.getElementById("original-version");
      if (!optionsWrapper) {
        return;
      }

      const productVariants = this.productData.variants;
      const fieldsets = optionsWrapper.querySelectorAll("fieldset");

      // Get currently selected Frame Color and Lens Options
      const currentSelections = {};
      this.productData.options.forEach((optionName, index) => {
        const fieldset = fieldsets[index];
        const checkedRadio = fieldset?.querySelector(
          'input[type="radio"]:checked'
        );
        if (checkedRadio && optionName !== "Frame Size") {
          currentSelections[optionName] = checkedRadio.value;
        }
      });

      // Only proceed if we have both Frame Color and Lens Options selected
      // Otherwise, we can't determine which frame sizes should be hidden
      const hasFrameColor = currentSelections["Frame Color"];
      const hasLensOption =
        currentSelections["Lens Options"] || currentSelections["Lens Color"];

      if (!hasFrameColor || !hasLensOption) {
        logV2Pdp(
          "[Availability] Skipping frame size availability check - missing required selections"
        );
        return;
      }

      // Find the Frame Size fieldset
      const frameSizeFieldset = Array.from(fieldsets).find((fs) => {
        const input = fs.querySelector('input[type="radio"]');
        return input && input.name === "frame-size";
      });

      if (!frameSizeFieldset) return;

      // Check each frame size option
      const frameSizeInputs = frameSizeFieldset.querySelectorAll(
        'input[type="radio"]'
      );

      const availableFrameSizes = [];

      frameSizeInputs.forEach((input) => {
        const frameSize = input.value;

        // Build test criteria with this frame size
        const testCriteria = { ...currentSelections };
        testCriteria["Frame Size"] = frameSize;

        // Check if there's at least one AVAILABLE and NON-EOL variant matching this criteria
        // Also collect all matching variants (even out of stock) to determine if option should be shown
        let matchingVariants = [];
        let hasAvailableVariant = false;

        for (const variant of productVariants) {
          // First check if variant is EOL - exclude it immediately
          if (variant.sku && this.EOL_SKUS.includes(String(variant.sku))) {
            continue;
          }

          // Then check if variant matches our criteria
          let isMatch = true;
          for (const key in testCriteria) {
            const optionIndex = this.productData.options.indexOf(key);
            if (variant.options[optionIndex] !== testCriteria[key]) {
              isMatch = false;
              break;
            }
          }

          if (isMatch) {
            matchingVariants.push({
              id: variant.id,
              available: variant.available,
              sku: variant.sku,
            });
            if (variant.available) {
              hasAvailableVariant = true;
            }
          }
        }

        const hasMatchingVariant = matchingVariants.length > 0;

        // Update UI to show/hide frame size options based on EOL and availability
        // Note: We don't mark frame sizes as "out of stock" - OOS state is controlled by the Buy Now button
        const label = input.closest(".size-option");
        if (label) {
          if (hasAvailableVariant) {
            // Available: show the option and remove EOL hidden class
            label.classList.remove("eol-hidden");
            label.classList.remove("is-out-of-stock");
            input.disabled = false;
            availableFrameSizes.push({ input, frameSize });
            logV2Pdp(`[Frame Size] ${frameSize} is available - showing option`);
          } else if (hasMatchingVariant) {
            // Out of stock but variant exists: show the option (no OOS styling - controlled by Buy Now button)
            // This matches collection card behavior where frame sizes are shown regardless of availability
            label.classList.remove("eol-hidden");
            label.classList.remove("is-out-of-stock"); // Remove any OOS styling
            input.disabled = false; // Allow selection even if out of stock (for "Notify when in stock")
            logV2Pdp(
              `[Frame Size] ${frameSize} is out of stock but showing option`
            );
          } else {
            // No matching non-EOL variants: hide the option completely (variant doesn't exist)
            label.classList.add("eol-hidden");
            label.classList.remove("is-out-of-stock"); // Remove OOS styling even when hiding
            input.disabled = true;
            logV2Pdp(
              `[Frame Size] ${frameSize} has no matching non-EOL variants - hiding option`
            );
          }
        }

        // Update "What's my size?" modal
        // Always show frame sizes in the popup (don't hide based on availability)
        const popupRadio = document.querySelector(
          `.wms-modal__radio-input[value="${frameSize}"]`
        );
        const popupRadioGroup = popupRadio?.closest(".wms-modal__radio-group");

        if (popupRadioGroup) {
          // Always show frame sizes in the popup, regardless of availability
          // Only hide if variant doesn't exist (EOL or no matching variant)
          if (hasMatchingVariant) {
            popupRadioGroup.classList.remove("eol-hidden");
            popupRadioGroup.classList.remove("is-out-of-stock");
            if (popupRadio) popupRadio.disabled = false;
            logV2Pdp(
              `[Availability] ${frameSize} shown in modal (available: ${hasAvailableVariant})`
            );
          } else {
            // Only hide if variant doesn't exist at all
            popupRadioGroup.classList.add("eol-hidden");
            popupRadioGroup.classList.remove("is-out-of-stock");
            if (popupRadio) popupRadio.disabled = true;
            logV2Pdp(
              `[Availability] ${frameSize} has no matching variants - hiding from modal`
            );
          }
        }

        // Update sizing-stack accordion (supports both old and new HTML structures)
        // Try new structure first: .sizing-stack__size-option-inner
        let sizingStackInput = document.querySelector(
          `.sizing-stack__size-option-inner input[value="${frameSize}"]`
        );
        let sizingStackOption = sizingStackInput?.closest(
          ".sizing-stack__size-option"
        );

        // Fallback to old structure: label > input
        if (!sizingStackInput) {
          sizingStackInput = document.querySelector(
            `.sizing-stack__size-option input[value="${frameSize}"]`
          );
          sizingStackOption = sizingStackInput?.closest(
            ".sizing-stack__size-option"
          );
        }

        if (sizingStackOption) {
          // Show frame sizes in sizing-stack if variant exists, regardless of availability
          if (hasMatchingVariant) {
            sizingStackOption.classList.remove("eol-hidden");
            sizingStackOption.classList.remove("is-out-of-stock");
            if (sizingStackInput) sizingStackInput.disabled = false;
            logV2Pdp(
              `[Availability] ${frameSize} shown in sizing-stack (available: ${hasAvailableVariant})`
            );
          } else {
            // Only hide if variant doesn't exist at all
            sizingStackOption.classList.add("eol-hidden");
            sizingStackOption.classList.remove("is-out-of-stock");
            if (sizingStackInput) sizingStackInput.disabled = true;
            logV2Pdp(
              `[Availability] ${frameSize} has no matching variants - hiding from sizing-stack`
            );
          }
        }
      });

      // Note: Frame size fieldset should remain hidden by default (has "hide-this" class)
      // It will only be revealed when:
      // 1. User comes from collection with ?frame_size= parameter (handled by revealFrameSizeFromUrl())
      // 2. User opens "What's my size?" popup and selects a size (handled in popup logic)

      // Auto-select logic: If current selection is out of stock/disabled or no selection exists,
      // and there's at least one available option, select it
      const currentlyChecked = frameSizeFieldset.querySelector(
        'input[type="radio"]:checked'
      );
      const isCurrentDisabled = currentlyChecked?.disabled;

      if (
        availableFrameSizes.length > 0 &&
        (!currentlyChecked || isCurrentDisabled)
      ) {
        // Prefer "Regular" if available, otherwise pick the first available
        const preferredSize =
          availableFrameSizes.find((item) => item.frameSize === "Regular") ||
          availableFrameSizes[0];

        if (preferredSize && preferredSize.input) {
          logV2Pdp(
            `[Availability] Auto-selecting frame size: ${preferredSize.frameSize}`
          );
          preferredSize.input.checked = true;

          // Dispatch change event to trigger variant update
          preferredSize.input.dispatchEvent(
            new Event("change", { bubbles: true })
          );

          // After auto-selecting, verify the variant and update OOS status
          setTimeout(() => {
            const currentVariant = this.getCurrentVariant();
            if (currentVariant) {
              this.checkAndUpdateOOSStatus(currentVariant);
            }
          }, 200);
        }
      }
    } catch (error) {
      logError("updateFrameSizeAvailability error:", error);
    }
  },

  /**
   * Setup VTO tab toggle for Dolomite Slate variants
   */
  setupVTOTabToggle() {
    // Initial check
    this.checkVTOTabVisibility();

    // Listen for popstate events
    window.addEventListener("popstate", () => this.checkVTOTabVisibility());
  },

  /**
   * Check and toggle VTO tab visibility based on current variant
   */
  checkVTOTabVisibility() {
    const vtoTab = document.querySelector('[data-accordion-type="vto"]');
    if (!vtoTab) return;

    const slateDolomiteVariants = ["47634139250939", "47634141643003"];
    const productHandle = this.productData?.handle;

    const queryParams = new URLSearchParams(window.location.search);
    const variantId = queryParams.get("variant");

    const isDolomiteSlate =
      productHandle === "dolomite-ombraz-armless-sunglasses" &&
      slateDolomiteVariants.includes(variantId);

    if (isDolomiteSlate) {
      vtoTab.classList.add("hidden");
    } else {
      vtoTab.classList.remove("hidden");
    }
  },
};

// Bootstrap
document.addEventListener("DOMContentLoaded", v2PdpJs.init.bind(v2PdpJs));
window.addEventListener("beforeunload", v2PdpJs.destroy.bind(v2PdpJs));
